// ============================================================================
// CE QUE LA LISTE DES ARTICLES ET LA FICHE ARTICLE RENDENT DE LA MÊME FAÇON.
//
// ⚠️ EXTRAIT AVANT D'ÉCRIRE LA FICHE, et pas après : la matrice des déclinaisons recopiée dans deux
// écrans aurait divergé. Ce n'est pas une crainte, c'est un constat — le marqueur « ≠ » a été écrit
// deux fois dans ce même écran, et l'une des deux copies ne l'affichait pas du tout (colonne Saison).
//
// ⚠️ CE QUI VIT ICI : uniquement ce qui a DEUX lecteurs. Les colonnes du tableau, l'en-tête de page et
// la modale de reclassement restent dans l'écran — un module « partagé » qui absorbe tout redevient le
// fichier qu'on vient de découper.
// ============================================================================
import { Fragment, useEffect, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from '@orgazone/money-format';
import { Badge, Copyable, Icon, Picker, Segmented, showToast, Tooltip, Trans, type IconName } from '@orgazone/ui-web';
import { api, type ArticleVariantSet, type FacetValue, type SecondAxisOption, type SecondDimension, type SizeGridOption, type StockMovementRow, type StockMovementSet, type StockStateSet, type StockCell } from '../api';
import { key, t, type Translatable } from '../i18n';
import {
  EMPTY_VARIANT_DRAFT, SANS, axesSummary, blockTotal, cellState, draftWithCode, industryLabel, lineToCreate, scanConflict,
  secondAxisBlocker, secondAxisRows, sharedCases, sheetSizeColumns, sizeColumns, variantKey, type DraftCode, type VariantDraft,
} from './articles-variants-axes';
import { fieldLabel } from '../vocabulary';

/** Valeur absente : un tiret gris qui se distingue d'un zéro ou d'une valeur vide. */
export const Absent = () => <span style={{ color: 'var(--ink-300)' }}>—</span>;

/**
 * « ≠ » — les déclinaisons de ce modèle ne portent pas toutes la même valeur.
 * ⚠️ UN SEUL ENDROIT pour ce marqueur : écrit deux fois (une par famille de colonne), les deux copies
 * divergent — c'est déjà ce qui est arrivé, la colonne Saison ne l'affichait pas du tout.
 */
export function Divergent({ on }: { on: boolean }) {
  if (!on) return null;
  return (
    <span title={t('Les déclinaisons de ce modèle ne portent pas toutes la même valeur.')} style={{ color: 'var(--amber-fg)', marginLeft: 4, flexShrink: 0 }}>
      ≠
    </span>
  );
}

/**
 * Un libellé de modèle. `mixed` = les déclinaisons ne portent pas toutes la même valeur.
 * ⚠️ Le signaler plutôt qu'en élire une : le cas existe dans la donnée reprise, et afficher « Vestes »
 * pour un modèle qui vit dans deux familles serait une donnée choisie au hasard, montrée comme un fait.
 */
export function Texte({ v, mixed }: { v: string | null; mixed: boolean }) {
  if (!v) return <Absent />;
  return (
    // ⚠️ LES TROIS DÉCLARATIONS VONT ENSEMBLE : `overflow` ne s'applique PAS à une boîte inline, donc
    // l'ellipse écrite ici n'a jamais tronqué quoi que ce soit — le texte débordait ou s'enroulait,
    // et le sujet avait l'air traité. Il faut un conteneur non-inline, et `nowrap` pour que
    // `text-overflow` soit atteignable.
    <span style={{ display: 'flex', alignItems: 'baseline', minWidth: 0 }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{v}</span>
      {/* Le marqueur reste HORS du texte tronqué : à l'intérieur, il disparaîtrait exactement quand
          la valeur est longue — c'est-à-dire quand il est le plus utile. */}
      <Divergent on={mixed} />
    </span>
  );
}

/**
 * Une valeur, ou la FOURCHETTE quand les déclinaisons divergent — jamais l'une des deux prise pour l'autre.
 * ⚠️ COMPARE LES VALEURS RENDUES, pas les entiers : deux montants fins distincts (125100 / 125149)
 * s'affichent identiques au centime, et « 12,51 – 12,51 » se lit comme un bug d'écran.
 */
export function Fourchette({ min, max, rendu }: { min: number; max: number; rendu: (v: number) => string }) {
  const a = rendu(min), b = rendu(max);
  // ⚠️ Le symbole ne part JAMAIS à la ligne : la cellule est étroite, et « 689,00 » sur une ligne avec
  // « € » sur la suivante se lit comme deux valeurs.
  return <span style={{ whiteSpace: 'nowrap' }}>{a === b ? a : `${a} – ${b}`}</span>;
}

/**
 * ⚠️ SEUIL DE STOCK FAIBLE — en dur ici, et c'est une DETTE ASSUMÉE : la spec le dit paramétrable par
 * magasin (backoffice.md §299), et le nombre existe déjà en plusieurs copies dans le dépôt. Il sera lu
 * du réglage du magasin quand l'écran Paramètres existera ; le nommer une fois évite au moins d'en
 * fabriquer une copie de plus dans le libellé de l'infobulle.
 */
export const LOW_STOCK = 5;

/**
 * Le stock du modèle.
 *
 * ⚠️ UN ZÉRO S'AFFICHE, MÊME QUAND RIEN N'A JAMAIS ÉTÉ COMPTÉ (décision utilisateur 2026-08-17 :
 * « le 0 est plus parlant »). Ce fichier rendait un tiret dans ce cas, pour ne pas affirmer une rupture
 * non constatée — la nuance est réelle, mais elle coûtait plus qu'elle ne rapportait : sur une liste de
 * 30 000 articles, un tiret se lit « inconnu » et fait chercher une donnée manquante là où il n'y a
 * simplement rien en réserve.
 *
 * ⚠️ LA NUANCE N'EST PAS PERDUE, elle change de canal : les deux zéros portent une INFOBULLE
 * différente, et le « jamais compté » reste en encre effacée. Ce qui disparaît est la distinction à
 * l'œil nu, pas l'information.
 */
export function Stock({ qty }: { qty: number | null }) {
  if (qty === null) {
    return (
      <span
        title={t('Aucun mouvement de stock n’a jamais été enregistré pour ce modèle dans ce magasin : rien n’a été compté, ce n’est pas une rupture constatée.')}
        style={{ color: 'var(--ink-400)' }}
      >0</span>
    );
  }
  // ⚠️ UN STOCK NÉGATIF SE MONTRE : le repli `qty <= 0 → « 0 »` affichait un zéro rassurant sur une
  // ANOMALIE — des sorties enregistrées sans les entrées correspondantes (inventaire manquant,
  // réception jamais intégrée). C'est précisément ce qu'un gestionnaire doit voir.
  if (qty < 0) {
    return (
      <Badge tone="warn" size="sm" radius="ctrl" srLabel={t('stock négatif, à corriger')}
        title={t('Stock NÉGATIF : des sorties ont été enregistrées sans les entrées correspondantes. À corriger.')}>{qty}</Badge>
    );
  }
  if (qty === 0) return <span style={{ color: 'var(--ink-400)' }}>0</span>;
  // ⚠️ `srLabel` : sans lui l'alerte n'existe que dans la teinte ambre et un `title` posé sur un
  // élément non focalisable — donc pas du tout pour qui ne voit pas la couleur.
  if (qty < LOW_STOCK) {
    return (
      <Badge tone="warn" size="sm" radius="ctrl" srLabel={t('stock faible')}
        title={t('Stock faible : moins de {pieces} sur ce modèle.', { pieces: t('{n} pièce', { n: LOW_STOCK }) })}>{qty}</Badge>
    );
  }
  return <strong style={{ fontWeight: 700 }}>{qty}</strong>;
}

/**
 * Géométrie de la matrice — DEUX CRANS, relevés sur la maquette.
 * ⚠️ La fiche et le dépliage de ligne ne sont PAS le même objet : la fiche montre la matrice comme
 * sujet principal (vignette 128×74, relevé l.3606), le dépliage la glisse SOUS une rangée de tableau
 * et doit rester compact. Un seul cran obligerait l'un des deux à mentir sur son importance.
 */
const CRAN = {
  sheet: { w: 128, h: 74, couleur: 118, gap: 5 },
  depliage: { w: 82, h: 54, couleur: 128, gap: 6 },
} as const;
/**
 * Géométrie de la matrice À DEUX AXES — deux crans, relevés sur la maquette.
 * ⚠️ La fiche dessine un BLOC par couleur (la valeur de dimension en tête de ligne sur `axe` px, des
 * cases de `h` px sur au moins `w`) ; le dépliage de la liste dessine des LIGNES couleur × valeur (la
 * couleur puis la valeur en pastille sur `axe` px, des cases compactes). Deux arrangements du dessin,
 * une seule règle de rangement (`articles-variants-axes.ts`).
 */
const CRAN2 = {
  sheet: { h: 46, w: 72, axe: 64, gap: 4, fill: '1fr' },
  depliage: { h: 32, w: 44, axe: 128, gap: 4, fill: 'auto' },
} as const;
type Cran2 = (typeof CRAN2)[keyof typeof CRAN2];

/**
 * LES DÉCLINAISONS RÉELLES DU MODÈLE, en matrice couleur × taille.
 *
 * ⚠️ DEUX LECTEURS : le dépliage d'une ligne de la liste, et l'onglet Déclinaisons de la fiche. Même
 * route, même rendu, même légende — c'est tout l'objet de l'extraction.
 *
 * ⚠️ TROIS SIGNES DISTINCTS, et la légende les nomme : une case VIDE = cette déclinaison n'existe pas ;
 * « — » = elle existe mais n'a aucune ligne de stock ; « 0 » = son stock a été mesuré à zéro. La maquette
 * n'en distingue que deux (« le tiret signale une déclinaison à zéro, pas une déclinaison absente ») —
 * elle n'avait pas le cas « pas de ligne de stock », que la vraie base produit.
 *
 * ⚠️ `fiche` AJOUTE LA COQUILLE DE LA MAQUETTE (titre, bascule Matrice/Liste, total), pas une autre
 * matrice : sous une rangée de tableau, un titre « Déclinaisons » répéterait la ligne qu'on vient de
 * déplier, et un total ferait doublon avec la colonne Stock juste au-dessus.
 */
export function Variants({ modelKey, sheet = false, grid, grid2, draft, colorOffers, readVersion = 0, onDeleteVariant }: {
  modelKey: string;
  /**
   * SUPPRIMER UNE DÉCLINAISON (vue Liste de la fiche) — absent sans le droit : le geste n'est pas montré.
   * ⚠️ C'est l'outil qui reprend une déclinaison rattachée par erreur à un autre article ; le serveur juge si elle peut partir.
   */
  onDeleteVariant?: (gencode: string) => void;
  sheet?: boolean;
  /**
   * LES DÉCLINAISONS EN ATTENTE, tenues par la fiche — couleurs ajoutées et codes scannés, écrits par son pied.
   * ⚠️ ABSENT ⇒ NI « Scanner les gencodes » NI « Ajouter » : sous une rangée de tableau, personne n'enregistre.
   */
  draft?: { value: VariantDraft; onChange: (next: VariantDraft) => void };
  /** Les couleurs du référentiel, offertes par « Ajouter » — la fiche les a déjà lues pour ses filtres. */
  colorOffers?: readonly FacetValue[];
  /** Change après chaque enregistrement de la fiche : c'est ce qui fait relire les déclinaisons écrites. */
  readVersion?: number;
  /**
   * LA GRILLE DE TAILLES, quand l'écran hôte sait l'ÉCRIRE.
   * ⚠️ ABSENTE ⇒ PAS DE BARRE, et surtout pas une barre inerte : sous une rangée de tableau, personne
   * ne tient la carte de différences, donc rien ne pourrait enregistrer le choix. Un picker qui
   * n'écrit pas est exactement le « bouton qui fait semblant » de §23.0.
   */
  grid?: {
    valeur: string | null;
    mixte: boolean;
    editable: boolean;
    onChange: (v: string | null) => void;
    /**
     * CRÉER la grille tapée. ⚠️ ELLE ÉCRIT VRAIMENT (règle utilisateur 2026-08-17 : « les select de la
     * fiche sont mono-sélection MAIS AVEC création »). C'est aussi ce qui SUPPRIME le risque de clé
     * pendante que je redoutais : la clé étant dérivée du nom, créer la ligne visée la rend valide.
     */
    onCreate: (nom: string) => Promise<string>;
  };
  /**
   * LE SECOND AXE, jumeau de la grille — même carte de différences, même pied.
   * ⚠️ C'EST UN CHAMP D'ARTICLE, PAS UN GESTE DE RÉFÉRENTIEL : écrit sur la grille principale, il vaudrait pour
   * tous les modèles qui la portent.
   */
  grid2?: {
    valeur: string | null;
    mixte: boolean;
    editable: boolean;
    onChange: (v: string | null) => void;
    onCreate: (nom: string) => Promise<string>;
  };
}) {
  /** Les grilles offertes — chargées SEULEMENT si une barre les demande. */
  const [grids, setGrids] = useState<SizeGridOption[] | null>(null);
  /** Les grilles SECONDAIRES offertes au rattachement — servies dans le même chargement. */
  const [dimensions, setDimensions] = useState<SecondAxisOption[] | null>(null);
  const [gridsFailure, setGridsFailure] = useState<string | null>(null);
  /** Bump après une création de grille : c'est ce qui fait RELIRE la liste offerte. */
  const [gridsAttempt, setGridsAttempt] = useState(0);
  /** Le plafond de cases par envoi, servi avec les grilles — « Ajouter » le vérifie avant d'allouer des codes. */
  const [maxVariants, setMaxVariants] = useState<number | null>(null);
  /** Le mode scan : les cases deviennent des champs de saisie, sur toutes les tailles de la grille. */
  const [scan, setScan] = useState(false);
  /** Le panneau « Ajouter » : la couleur saisie et le choix des gencodes (éteint d'origine, comme au dessin). */
  const [ajout, setAjout] = useState<{ couleur: string; auto: boolean } | null>(null);
  /** L'allocation des gencodes automatiques est en vol. */
  const [writing, setWriting] = useState(false);
  const [writeFailure, setWriteFailure] = useState<string | null>(null);
  /** Le dernier scan refusé : la case, le code lu, et la déclinaison qui le porte déjà. */
  const [scanError, setScanError] = useState<{ key: string; code: string; where: string } | null>(null);
  /** La couleur qu'on vient d'ajouter sans gencodes : son premier champ de scan prend le curseur. */
  const [focusColor, setFocusColor] = useState<string | null>(null);
  /** Matrice ou liste — la bascule de la maquette. La liste ajoute PRIX et ÉTAT par déclinaison. */
  const [vue, setVue] = useState<'matrice' | 'liste'>('matrice');
  const [jeu, setJeu] = useState<ArticleVariantSet | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    api.articleVariants(modelKey)
      .then((r) => { if (vivant) { setJeu(r); setError(null); } })
      .catch((e: unknown) => { if (vivant) setError(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [modelKey, readVersion]);

  // ⚠️ LE CURSEUR VA À LA PREMIÈRE CASE DE LA COULEUR AJOUTÉE (maquette : `fAddGo` sans gencodes automatiques) — on
  //    vient l'ajouter pour la scanner, et la douchette doit pouvoir tirer sans un clic de plus.
  useEffect(() => {
    if (!focusColor || !scan) return;
    const first = Array.from(document.querySelectorAll<HTMLInputElement>('input[data-scan]'))
      .find((el) => el.dataset.scanColor === focusColor);
    first?.focus();
    first?.select();
    setFocusColor(null);
  }, [focusColor, scan]);

  /**
   * Les grilles offertes.
   * ⚠️ L'ÉCHEC EST RETENU, PAS AVALÉ : sans grilles, le picker ne peut rien offrir, et « aucune grille »
   * se lirait « ce magasin n'en a pas » — l'opérateur en créerait une qui existe déjà.
   *
   * ⚠️ LA DÉPENDANCE EST UN BOOLÉEN, PAS L'OBJET (correctif). `grille` est un objet littéral que l'hôte
   * recrée à CHAQUE rendu : son identité change à chaque fois, donc l'effet repartait à chaque rendu et
   * rappelait le serveur en boucle. Un défaut invisible à l'œil — l'écran est juste — et qui ne se voit
   * que dans les logs du serveur ou sur la facture.
   *
   * ⚠️ RELUES APRÈS CRÉATION (`essaiGrilles`) : une grille créée depuis le panneau restait absente de
   * sa propre liste jusqu'à la réouverture de la fiche.
   */
  const gridsOffer = !!grid;
  useEffect(() => {
    if (!gridsOffer) return;
    let vivant = true;
    api.articleSizeGrids()
      .then((r) => { if (vivant) { setGrids(r.grids); setDimensions(r.dimensions); setMaxVariants(r.maxVariants); setGridsFailure(null); } })
      .catch((e: unknown) => { if (vivant) setGridsFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [gridsOffer, gridsAttempt]);

  /** La grille CHOISIE (celle en cours d'édition), telle que la base la connaît. */
  const grilleChoisie = (grids ?? []).find((g) => g.name === grid?.valeur) ?? null;
  /** Le SECOND AXE choisi, tel que la base le connaît — une liste indépendante de la première. */
  const secondAxe = (dimensions ?? []).find((d) => d.name === grid2?.valeur) ?? null;

  /**
   * POURQUOI le second axe n'est pas offert — et `null` quand il l'est, donc que le picker se dessine.
   * ⚠️ LA PHRASE EST CALCULÉE, PAS DESSINÉE : c'est elle que la garde mesure, sans monter d'écran.
   */
  const axe2Blocage = grid2
    ? secondAxisBlocker({ mixedAxes: grid2.mixte, gridsLoaded: grids !== null })
    : null;

  if (error) {
    return (
      <div role="alert" style={{ padding: '10px 4px', color: 'var(--danger-ink)', fontSize: 'var(--t-note)' }}>
        
        {t('Les déclinaisons de ce modèle n’ont pas pu être lues : {error}', { error })}
      </div>
    );
  }
  if (!jeu) return <div style={{ padding: '10px 4px', color: 'var(--ink-500)', fontSize: 'var(--t-note)' }}>{t('Lecture des déclinaisons…')}</div>;
  const rows = jeu.rows;
  if (rows.length === 0) {
    return <div style={{ padding: '10px 4px', color: 'var(--ink-500)', fontSize: 'var(--t-note)' }}>{t('Ce modèle ne porte aucune déclinaison en base.')}</div>;
  }

  const brouillon = draft?.value ?? EMPTY_VARIANT_DRAFT;
  // ⚠️ LA MATRICE SUIT LES GRILLES CHOISIES, enregistrées ou non (maquette : `fArt` lit `fEdit.tgrid`, `d2Of` lit
  //    `fEdit.lgrid`) : choisir une grille montre aussitôt ses tailles, et le scan y crée celles qui manquent. Tant
  //    que la liste des grilles n'est pas lue, on dessine celles que les déclinaisons portent en base.
  const gridSizes = !grid || grids === null
    ? jeu.sizeOrder
    : grid.valeur === null ? [] : (grilleChoisie?.sizes ?? jeu.sizeOrder);
  const dimension: SecondDimension | null = !grid2 || dimensions === null || grid2.valeur === (jeu.dimension?.name ?? null)
    ? jeu.dimension
    : secondAxe;
  // ⚠️ LES TAILLES SONT TOUJOURS EN COLONNES, dans l'ORDRE DÉCLARÉ PAR LA GRILLE — rangé par l'alphabet, un tableau
  //    donnerait L avant M et S avant XS. La fiche suit la règle de la maquette (toute la grille en scan) ; le
  //    dépliage d'une ligne de la liste ne montre que ce qui existe.
  const sizes = sheet ? sheetSizeColumns(gridSizes, rows, brouillon, scan) : sizeColumns(rows, jeu.sizeOrder);
  const enBase = [...new Set(rows.map((v) => v.color ?? SANS))];
  // ⚠️ LES COULEURS EN ATTENTE SONT DESSINÉES, PAS ÉCRITES : leurs cases restent en pointillé jusqu'au scan, et
  //    rien n'est en base avant « Enregistrer ».
  const enAttente = [...brouillon.colors, ...Object.values(brouillon.codes).map((c) => c.color ?? SANS)];
  const couleurs = [...enBase, ...[...new Set(enAttente)].filter((c) => !enBase.includes(c))];
  // ⚠️ LE SECOND AXE VIENT DE LA GRILLE : ses valeurs font les lignes de chaque bloc de couleur. Sans
  // lui, `size2` n'entre pas dans la clé — une déclinaison qui en porterait un quand même (reprise
  // incomplète) tombe dans la case de sa taille au lieu de disparaître de la matrice.
  const valeurs = dimension ? secondAxisRows(rows, dimension) : [];
  const cellule = new Map(rows.map((v) => [variantKey(v.color, v.size, dimension ? v.size2 : null), v]));
  /** Les déclinaisons qu'une case cache — la matrice n'en montre qu'une par case, ce compte le dit. */
  const shared = sharedCases(rows, !!dimension);
  const hidden = [...shared.values()].reduce((a, b) => a + b, 0);
  const cran = sheet ? CRAN.sheet : CRAN.depliage;
  const cran2 = sheet ? CRAN2.sheet : CRAN2.depliage;
  // ⚠️ LE TOTAL SOMME CE QUI EST MESURÉ, et `null` n'est pas zéro : une déclinaison sans ligne de stock
  // n'a jamais été comptée. La compter pour 0 ferait d'une ABSENCE DE MESURE une mesure à zéro.
  const totalStock = rows.reduce((n, v) => n + (v.stockQty ?? 0), 0);
  const jamaisCompte = rows.filter((v) => v.stockQty === null).length;
  /** Une couleur déjà portée par la fiche, en base ou en attente — la comparaison de la maquette (`keyOf`). */
  const colorTaken = (c: string): boolean => couleurs.some((x) => x.trim().toLowerCase() === c.trim().toLowerCase());

  /**
   * UN CODE VALIDÉ PAR ENTRÉE DANS UNE CASE — il va au brouillon, ou il est refusé s'il est déjà porté ailleurs sur
   * l'article (maquette : `fScanKey`). ⚠️ `size2` n'entre dans la case que si la matrice a un second axe, comme la clé.
   */
  const scanCode = (cell: { color: string; size: string; size2: string | null }, code: string, next: () => void, stay: () => void) => {
    if (!draft) return;
    const target = {
      color: cell.color === SANS ? null : cell.color,
      size: cell.size === SANS ? null : cell.size,
      size2: cell.size2 === null || cell.size2 === SANS ? null : cell.size2,
    };
    const key = variantKey(target.color, target.size, target.size2);
    const existing = cellule.get(variantKey(target.color, target.size, target.size2));
    const clash = code && code !== existing?.gencode ? scanConflict(rows, brouillon, key, code, !!dimension) : null;
    if (clash) {
      setScanError({ key, code, where: `${clash.color ?? SANS} · ${fieldLabel('size').toLowerCase()} ${industryLabel(clash.size, clash.size2)}` });
      stay();
      return;
    }
    setScanError(null);
    draft.onChange(draftWithCode(brouillon, target, existing, code));
    next();
  };

  /**
   * « CRÉER LES DÉCLINAISONS » du panneau « Ajouter » (maquette : `fAddGo`).
   * Avec les gencodes automatiques, toute la ligne de la grille entre au brouillon avec des codes RÉSERVÉS au serveur —
   * le dessin les montre, et un code montré doit être celui qui sera posé. Sans eux, la ligne s'ouvre en pointillé et le
   * scan part de sa première case.
   * ⚠️ DES CODES RÉSERVÉS PUIS ABANDONNÉS (« Annuler ») SONT PERDUS, et c'est la règle de l'allocation : la séquence est
   *    monotone, pas sans trou.
   */
  const addColor = async () => {
    if (!draft || !ajout || writing) return;
    const c = ajout.couleur.trim();
    if (!c || colorTaken(c)) return;
    if (!ajout.auto) {
      draft.onChange({ ...brouillon, colors: [...brouillon.colors, c] });
      setAjout(null);
      setScan(true);
      setFocusColor(c);
      showToast(t('{color} — scannez les gencodes de la ligne', { color: c }));
      return;
    }
    // ⚠️ LA LIGNE COUVRE TOUTE LA GRILLE, croisée avec toute la dimension — et sans grille, les tailles du modèle.
    const cells = lineToCreate(c, gridSizes.length > 0 ? gridSizes : sizeColumns(rows, []), dimension);
    if (maxVariants !== null && cells.length > maxVariants) {
      setWriteFailure(t('Cette couleur demande {declinaisons} : le maximum est de {max} par ajout.', { declinaisons: t('{n} déclinaison', { n: cells.length }), max: maxVariants }));
      return;
    }
    setWriting(true);
    setWriteFailure(null);
    try {
      const { gencodes } = await api.allocateGencodes(cells.length);
      let next: VariantDraft = brouillon;
      cells.forEach((cell, i) => { next = draftWithCode(next, cell, undefined, gencodes[i]!); });
      draft.onChange({ ...next, colors: [...next.colors, c] });
      setAjout(null);
      showToast(t('{color} — {declinaisons}, gencodes auto', { color: c, declinaisons: t('{n} déclinaison', { n: cells.length }) }));
    } catch (e) {
      // ⚠️ RIEN N'ENTRE AU BROUILLON : sans codes réservés, la ligne afficherait des codes qui n'existent pas.
      setWriteFailure(e instanceof Error ? e.message : String(e));
    } finally {
      setWriting(false);
    }
  };

  return (
    <div style={{ padding: sheet ? '2px 0 0' : '10px 4px 4px' }}>
      {sheet && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Déclinaisons')}</div>
            <p style={{ margin: '3px 0 0', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
              
              {t('Un code-barres par déclinaison — c’est lui que scanne la caisse.')}
              {/* ⚠️ LES AXES SE DISENT EN TÊTE (sous-titre de la maquette « Articles — Tailles 2 » : « grille Denim
                  homme — tailles × longueurs (dimension 2) » / « grille alpha — une dimension ») : sans cette
                  phrase, une matrice à un axe et une matrice dont la grille n'a pas de second axe se ressemblent,
                  et l'opérateur cherche des longueurs qui n'existent pas. */}
              {' '}
              <span data-testid="axes-declinaisons" style={{ color: gridSizes.length ? 'var(--ink-700)' : 'var(--amber-fg)' }}>
                {axesSummary(grid?.valeur ?? null, gridSizes, dimension)}
              </span>
            </p>
          </div>
          {/* ⚠️ UNE BASCULE DE VUE, PAS DES ONGLETS : les deux vues montrent LE MÊME jeu de
              déclinaisons sous deux angles. `role="tab"` y annoncerait un changement de contenu. */}
          <Segmented
            label={t('Affichage des déclinaisons')}
            value={vue}
            onChange={setVue}
            size="sm"
            options={[{ id: 'matrice', label: t('Matrice') }, { id: 'liste', label: t('Liste') }]}
          />
        </div>
      )}
      {/* ⚠️ LA TRONCATURE SE DIT AVANT LA MATRICE : sous la borne, des coloris ENTIERS manquent, et un
          stock absent de l'écran se lit « pas en stock ». */}
      {jeu.truncated && (
        <p role="status" style={{ margin: '0 0 8px', padding: '6px 10px', borderRadius: 6, background: 'var(--amber-bg)', color: 'var(--ink-800)', fontSize: 'var(--t-note)', lineHeight: 1.5 }}>
          
          {t('Ce modèle porte plus de {declinaisons} : seules les {n} premières sont affichées. Des coloris entiers peuvent manquer de cette matrice.', { declinaisons: t('{n} déclinaison', { n: jeu.limit }), n: jeu.limit })}
        </p>
      )}

      {/* ⚠️ LA BARRE D'OUTILS DE LA MAQUETTE : un bandeau tramé, les grilles à gauche, « Scanner les gencodes » et
          « Ajouter » à droite. */}
      {grid && (
        <div style={{
          // ⚠️ ANATOMIE RELEVÉE SUR LA MAQUETTE (bandeau des déclinaisons) : `align-items:flex-end` pour
          //    que les commandes s'alignent sur le BAS des champs, dont l'intitulé est au-dessus.
          display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 14,
          padding: '12px 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)',
          background: 'var(--ink-50)',
        }}>
          {/* ⚠️ LES DEUX CHAMPS SE PARTAGENT UNE GRILLE, ILS NE S'ALIGNENT PAS À LA FILE (relevé de la
              maquette : `flex:1 1 440px` + `repeat(auto-fit, minmax(200px,1fr))`). C'EST CE QUI RÈGLE
              LE DÉBORDEMENT — et je l'avais raté : deux couples « intitulé à côté du champ » mis bout à
              bout demandaient 936 px pour 850 utiles, ce que j'avais compensé en rétrécissant un
              contrôle. Ici les intitulés passent AU-DESSUS, les champs prennent la largeur qui reste, et
              à l'étroit ils passent à une colonne au lieu de casser le bandeau. */}
          <div style={{ flex: '1 1 440px', minWidth: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
          <div style={{ minWidth: 0 }}>
          {gridsFailure
            ? (
              // ⚠️ ON DIT L'ÉCHEC PLUTÔT QUE D'OFFRIR UNE LISTE VIDE : « aucune grille » se lirait
              // « ce magasin n'en a pas », et on en créerait une qui existe déjà.
              <span role="alert" style={{ fontSize: 'var(--t-note)', color: 'var(--danger-ink)' }}>
                
                {t('Les grilles n’ont pas pu être lues : {gridsFailure}', { gridsFailure })}
              </span>
            )
            : (
              <Picker
                id="fiche-grille-tailles"
                label={fieldLabel('sizeGrid')}
                value={grid.valeur ?? ''}
                onChange={(v) => grid.onChange(v || null)}
                allLabel={t('À choisir')}
                // ⚠️ `field` ET NON `toolbar` : ce contrôle ÉCRIT. En `toolbar`, le picker ajoute une
                // rangée « tout » qui RÉVOQUE UN FILTRE — un contresens ici, où le geste pose une valeur.
                // ⚠️ ET `field` PLUTÔT QUE `field-inline` : c'est l'habillage qui dessine l'INTITULÉ
                // AU-DESSUS et prend toute la largeur de sa cellule — l'anatomie du bandeau de la
                // maquette, et ce qui permet à DEUX champs de tenir là où deux couples « intitulé à
                // côté du champ » débordaient.
                variant="field"
                clearLabel={t('Aucune grille')}
                // ⚠️ CRÉER ÉCRIT UNE LIGNE `size_grid` par le producteur, donc par le journal.
                // ⚠️ ET LA GRILLE NAÎT VIDE : son nom seul ne dit pas ses tailles. Le libellé de
                // l'option le dira (« aucune taille déclarée ») tant qu'elles ne sont pas saisies aux
                // Référentiels — une grille sans tailles ne range rien, et ça doit se voir.
                // ⚠️ CRÉER PUIS RELIRE, ici aussi : l'hôte relit SES facettes, pas cette liste-ci.
                // Sans ce bump, la grille créée n'apparaissait pas dans le picker qui vient de la créer.
                onCreate={async (v) => {
                  const retenue = await grid.onCreate(v);
                  setGridsAttempt((n) => n + 1);
                  return retenue;
                }}
                options={(grids ?? []).map((g) => ({
                  // ⚠️ LE NOM EST LA VALEUR : c'est lui que le producteur reçoit, la clé en étant
                  // dérivée. Envoyer un identifiant obligerait l'écran à connaître le mintage des clés.
                  value: g.name,
                  // ⚠️ ET LE LIBELLÉ MONTRE LES BORNES — « Vêtements FR (XS → XXL) », comme la maquette :
                  // deux grilles aux noms voisins peuvent n'avoir aucune taille commune.
                  label: g.sizes.length
                    ? `${g.name} (${g.sizes[0]} → ${g.sizes[g.sizes.length - 1]})`
                    : t('{name} (aucune taille déclarée)', { name: g.name }),
                }))}
                // ⚠️ `loading` tant que les grilles n’arrivent pas, et le picker le DIT : une liste
                // vide pendant le chargement se lirait « ce magasin n’a aucune grille ».
                loading={grids === null}
                unavailable={!grid.editable}
              />
            )}
          </div>
          <div style={{ minWidth: 0 }}>
          {/* ── LE SECOND AXE DU MODÈLE ───────────────────────────────────────────────────────────
              ⚠️ C'EST UN CHAMP DE L'ARTICLE, exactement comme la grille de tailles : il part avec le
              pied de la fiche, et il ne vaut que pour CE modèle. Il s'écrivait sur `size_grid.grid2_id`,
              donc sur tous les modèles de la grille — un pantalon donnait des longueurs aux tee-shirts.
              ⚠️ LES DEUX CONTRÔLES ONT DÉSORMAIS LE MÊME TEMPS. Voisins dans la même barre, ils
              écrivaient sur deux objets à deux moments différents ; rien à l'écran ne distinguait un
              choix différé d'un choix appliqué.
              ⚠️ CE QUE L'ÉCRAN DOIT DIRE, IL LE DIT EN INFOBULLE, PAS EN PHRASE (retour utilisateur
              2026-09-13 : « les textes cassent complètement l'interface ») — deux paragraphes dans une
              barre à `flexWrap` repoussaient les commandes à la ligne.
              ⚠️ LE CHOIX EST TOUJOURS DESSINÉ, INERTE QUAND IL NE PEUT PAS SERVIR : le faire
              disparaître était le défaut précédent — on cherchait la fonction ailleurs. */}
          {grid2?.editable && (
            <Tooltip
                label={axe2Blocage ? t('dimension 2 indisponible') : t('portée du second axe')}
                content={axe2Blocage ?? t('Ce choix s’écrit sur CE MODÈLE, pas sur la grille « {grille} » : les autres modèles qui la portent ne changent pas.', { grille: grilleChoisie?.name ?? '—' })}
                style={{ flexShrink: 0 }}
              >
                <Picker
                  id="fiche-dimension-2"
                  label={secondAxe ? secondAxe.label : t('Longueurs')}
                  value={secondAxe?.name ?? ''}
                  onChange={(v) => grid2.onChange(v || null)}
                  // ⚠️ LE VIDE DIT CE QU'IL SIGNIFIE (mot de la maquette) : « Aucune » seul laisse
                  //    croire à une valeur manquante ; ici il veut dire que le modèle n'a qu'UN axe.
                  allLabel={t('Aucune — une dimension')}
                  variant="field"
                  clearLabel={t('Aucune — une dimension')}
                  // ⚠️ CRÉER une dimension écrit une grille d'axe SECONDAIRE par le producteur ; le
                  //    modèle la prend ensuite par le pied de la fiche, comme n'importe quel champ.
                  //    Elle naît VIDE — ses valeurs se saisissent aux Référentiels, et le libellé le dit.
                  onCreate={grid2.onCreate}
                  options={(dimensions ?? []).map((d) => ({
                    value: d.name,
                    label: d.values.length ? `${d.name} (${d.values.join('/')})` : t('{name} (aucune valeur déclarée)', { name: d.name }),
                  }))}
                  loading={grids === null}
                  unavailable={axe2Blocage !== null}
                  unavailableReason={axe2Blocage ?? undefined}
                />
            </Tooltip>
          )}
          </div>
          </div>
          {/* ⚠️ LE POUSSOIR EST UN ÉLÉMENT (relevé l.3455 : «flex:1;min-width:8px«), pas un
              «margin-left:auto« posé sur un bouton : la maquette groupe les commandes à droite. */}
          <div style={{ flex: 1, minWidth: 8 }} />
          {/* ⚠️ « SCANNER » N'APPARAÎT QU'EN MATRICE (relevé : «sc-if fVGrid«) : en liste, il n'y a pas
              de vignette où poser le curseur, donc rien à scanner. */}
          {grid.editable && draft && vue === 'matrice' && (
            <button
              type="button"
              className={scan ? 'btn-cta' : 'btn-ghost'}
              style={{ height: 36 }}
              onClick={() => { setScan((v) => !v); setWriteFailure(null); setScanError(null); }}
            >
              <Icon name="barcode" size={15} style={{ marginRight: 7 }} />
              {scan ? t('Terminer le scan') : t('Scanner les gencodes')}
            </button>
          )}
          {grid.editable && draft && (
            <button
              type="button"
              className="btn-ghost"
              style={{ height: 36, borderColor: 'var(--teal-400)', color: 'var(--teal-700)' }}
              // ⚠️ L'INTERRUPTEUR NAÎT ÉTEINT, comme au dessin (`fAddOpen`) : sans lui, la ligne attend les codes du
              //    fournisseur, et un code maison ne se pose que si on le demande.
              onClick={() => { setAjout({ couleur: '', auto: false }); setWriteFailure(null); }}
            >
              
              {t('Ajouter')}
            </button>
          )}
          {grid.mixte && (
            // ⚠️ DEUX GRILLES SUR UN MÊME MODÈLE, c'est une matrice dont les colonnes ne veulent rien
            // dire. Ça se signale, et l'écriture qui suivra les alignera — irréversiblement.
            <span style={{ fontSize: 'var(--t-note)', color: 'var(--amber-fg)', fontWeight: 600 }}>
              
              {t('Les déclinaisons de ce modèle ne sont pas toutes sur la même grille.')}
            </span>
          )}
        </div>
      )}


      {/* ⚠️ LE PANNEAU « AJOUTER » (maquette : `fAddOn`) — il ajoute UNE COULEUR sur toute la grille.
          L'interrupteur décide de QUI fournit les codes-barres : nos codes maison, ou ceux du fournisseur
          qu'on va scanner un par un. */}
      {ajout && grid && draft && (
        <div style={{
          border: '1px solid var(--teal-300)', background: 'var(--teal-100)', borderRadius: 'var(--r-md)',
          padding: '14px 16px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: 230 }}>
            <Picker
              id="fiche-ajout-couleur"
              variant="field"
              label={fieldLabel('color')}
              allLabel={t('À choisir')}
              // ⚠️ LES COULEURS DU RÉFÉRENTIEL, comme au dessin (`colorRef`), et au-delà par la recherche : la liste
              //    d'un magasin dépasse le plafond des facettes, et sans elle on conclurait qu'une couleur n'existe pas.
              options={(colorOffers ?? []).map((v) => ({ value: v.value, label: v.value }))}
              onSearch={(q) => api.articleFacetSearch('coul', q).then((r) => r.map((v) => ({ value: v.value, label: v.value })))}
              value={ajout.couleur}
              onChange={(v) => setAjout({ ...ajout, couleur: v })}
              // ⚠️ « Créer » RETIENT la couleur tapée, comme l'assistant Gamme : elle devient réelle avec ses
              //    déclinaisons. Le sélecteur garde sa garde contre les quasi-doublons, qui est tout l'enjeu ici.
              onCreate={(v) => Promise.resolve(v.trim())}
              createLabel={t('Utiliser')}
            />
          </div>

          {/* ⚠️ L'INTERRUPTEUR DIT QUI FOURNIT LES CODES, et l'aide en dessous dit ce qui va se passer —
              deux comportements réellement différents, pas une préférence d'affichage. */}
          <button
            type="button"
            role="switch"
            aria-checked={ajout.auto}
            onClick={() => setAjout({ ...ajout, auto: !ajout.auto })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 9, height: 40, padding: '0 13px',
              border: '1px solid var(--teal-300)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            <span aria-hidden style={{
              width: 34, height: 20, borderRadius: 'var(--r-pill)', flexShrink: 0, display: 'flex', alignItems: 'center',
              padding: 3, background: ajout.auto ? 'var(--teal-500)' : 'var(--ink-200)',
              justifyContent: ajout.auto ? 'flex-end' : 'flex-start', transition: 'background 130ms',
            }}>
              <span style={{ width: 14, height: 14, borderRadius: 'var(--r-pill)', background: '#fff' }} />
            </span>
            <span style={{ fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-700)', whiteSpace: 'nowrap' }}>
              
              {t('Gencodes automatiques')}
            </span>
          </button>

          <button
            type="button"
            className="btn-cta"
            style={{ height: 40 }}
            aria-disabled={!ajout.couleur.trim() || colorTaken(ajout.couleur) || writing || undefined}
            onClick={() => { void addColor(); }}
          >
            {writing ? t('Création…') : t('Créer les déclinaisons')}
          </button>
          <button type="button" className="btn-ghost" style={{ height: 40 }} onClick={() => setAjout(null)}>

            {t('Annuler')}
          </button>

          <span style={{ width: '100%', fontSize: 'var(--t-meta)', color: 'var(--teal-800)', lineHeight: 1.5 }}>
            {ajout.auto
              ? (dimension && dimension.values.length > 0
                ? t('Une ligne complète est créée sur la grille — un gencode automatique par taille et par {champ}, stock à zéro.', { champ: dimension.label.toLowerCase() })
                : t('Une ligne complète est créée sur la grille — un gencode automatique par taille, stock à zéro.'))
              : t('La ligne est créée sans gencodes : le mode scan s’ouvre sur la première tuile de la couleur pour scanner les gencodes fournisseur, taille par taille.')}
            {ajout.couleur.trim() !== '' && colorTaken(ajout.couleur) && (
              <strong style={{ color: 'var(--amber-fg)' }}> {t('Cette couleur existe déjà sur la fiche.')}</strong>
            )}
          </span>
        </div>
      )}

      {/* ⚠️ LE BANDEAU DE SCAN, mot pour mot ce que la maquette explique : c'est un MODE, et un mode
          qui ne se dit pas se subit. */}
      {scan && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 9, padding: '11px 14px',
          border: '1px solid var(--teal-300)', borderRadius: 'var(--r-sm)', background: 'var(--teal-100)',
          marginBottom: 12, fontSize: 'var(--t-meta)', color: 'var(--teal-800)', lineHeight: 1.55,
        }}>
          <Icon name="barcode" size={15} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>
            <Trans k='Cliquez la première vignette puis scannez : <1>Entrée</1> enregistre le gencode et passe à la vignette suivante. Scanner une vignette « non créée » crée la déclinaison (stock 0) ; une vignette laissée telle quelle est ignorée. La saisie manuelle fonctionne aussi.' parts={[(x) => <strong>{x}</strong>]} /></span>
        </div>
      )}

      {/* ⚠️ LE SCAN REFUSÉ (maquette : `fScanErrOn`) — le code lu et la déclinaison qui le porte déjà. Le curseur
          reste sur la case, le code sélectionné : on rescanne par-dessus. */}
      {scanError && (
        <div role="alert" style={{
          display: 'flex', alignItems: 'center', gap: 9, padding: '11px 14px', border: '1px solid var(--danger)',
          borderRadius: 'var(--r-sm)', background: 'var(--danger-bg)', marginBottom: 12, fontSize: 'var(--t-meta)',
          color: 'var(--danger-ink)', lineHeight: 1.5,
        }}>
          <Icon name="alert" size={16} style={{ flexShrink: 0 }} />
          <span>
            <Trans
              k="<1>Scan refusé</1> — le gencode <2>{code}</2> est déjà affecté à <3>{where}</3> sur cet article."
              vars={{ code: scanError.code, where: scanError.where }}
              parts={[(x) => <strong>{x}</strong>, (x) => <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{x}</span>, (x) => <strong>{x}</strong>]}
            />
          </span>
        </div>
      )}

      {/* ⚠️ L'ÉCHEC PORTE LE MESSAGE RÉEL DU SERVEUR — une réservation de codes refusée, une ligne trop longue. Un
          « impossible » générique ferait deviner quoi corriger. */}
      {writeFailure && (
        <p role="alert" style={{
          margin: '0 0 12px', padding: '10px 13px', borderRadius: 'var(--r-sm)',
          background: 'var(--danger-bg)', color: 'var(--danger-ink)', fontSize: 'var(--t-note)', lineHeight: 1.5,
        }}>
          {writeFailure}
        </p>
      )}

      {/* ⚠️ UNE CASE N'EN MONTRE QU'UNE : quand plusieurs déclinaisons partagent une case, la matrice le DIT et renvoie à la
          vue Liste, qui les montre toutes — sans quoi une déclinaison existe sans se voir. */}
      {hidden > 0 && !(vue === 'liste' && sheet) && (
        <p role="status" style={{
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, margin: '0 0 12px', padding: '9px 12px',
          borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', border: '1px solid var(--amber)',
          color: 'var(--ink-800)', fontSize: 'var(--t-note)', lineHeight: 1.5,
        }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            {sheet
              ? t('{declinaisons} de plus partagent une case de la matrice, qui n’en montre qu’une : la vue Liste les montre toutes.', { declinaisons: t('{n} déclinaison', { n: hidden }) })
              : t('{declinaisons} de plus partagent une case de la matrice, qui n’en montre qu’une : la vue Liste de la fiche les montre toutes.', { declinaisons: t('{n} déclinaison', { n: hidden }) })}
          </span>
          {sheet && <button type="button" className="btn-ghost" style={{ height: 28, fontSize: '12px' }} onClick={() => setVue('liste')}>{t('Voir la liste')}</button>}
        </p>
      )}

      {vue === 'liste' && sheet
        ? <VariantsList rows={rows} pending={Object.values(brouillon.codes)} onDelete={onDeleteVariant} />
        : dimension
          ? (
            <MatriceDeuxAxes
              sheet={sheet} cran2={cran2} dimension={dimension} rows={rows}
              couleurs={couleurs} sizes={sizes} valeurs={valeurs} cellule={cellule} shared={shared}
              pending={brouillon.codes} scan={scan} scanErrorKey={scanError?.key ?? null}
              onScan={(c, size, l, code, next, stay) => scanCode({ color: c, size, size2: l }, code, next, stay)}
            />
          )
          : (
          <>
            {/* ⚠️ LES DEUX AXES, TOUJOURS : déclarer le seul X laisse le Y se calculer en `auto` (spec CSS),
                et un pixel de dépassement y fait apparaître un ascenseur vertical. */}
            <div style={{ overflowX: 'auto', overflowY: 'hidden', paddingBottom: 4 }}>
              {/* En-tête : la colonne des couleurs, puis une colonne par taille. */}
              <div style={{ display: 'flex', gap: cran.gap, marginBottom: cran.gap, alignItems: 'flex-end' }}>
                {/* ⚠️ LA COLONNE DE GAUCHE SE NOMME, ELLE AUSSI (relevé de la maquette, `axisL`/`axisFg` :
                    « Couleur » en encre pâle à un axe, « <Rubrique> ↓ » en violet à deux — le mot lui-même
                    vient du vocabulaire du magasin). Laissée vide,
                    la même matrice se lisait comme deux objets différents selon qu'elle a un second axe
                    ou non — alors que c'est le MÊME tableau, dont seules les lignes changent de nature. */}
                <div style={{
                  width: cran.couleur, flexShrink: 0, padding: '0 8px 3px', fontFamily: 'var(--font-mono)',
                  fontSize: '9.5px', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-400)',
                }}>{fieldLabel('color')}</div>
                {sizes.map((size) => (
                  <div key={size} style={{
                    width: cran.w, flexShrink: 0, textAlign: 'center', fontFamily: 'var(--font-mono)',
                    fontSize: '11.5px', fontWeight: 700, color: 'var(--ink-700)',
                  }}>{size}</div>
                ))}
              </div>

              {couleurs.map((c) => (
                <div key={c} style={{ display: 'flex', gap: cran.gap, marginBottom: cran.gap, alignItems: 'center' }}>
                  <div style={{
                    width: cran.couleur, flexShrink: 0, fontSize: '12.5px', fontWeight: 600,
                    color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{c}</div>
                  {sizes.map((size) => {
                    // ⚠️ LA CLÉ VIENT DU MODULE PUR, ICI AUSSI. Cette ligne a composé « couleur|taille » à
                    //    la main jusqu'au 2026-09-13, alors que la map porte TROIS composants depuis
                    //    l'arrivée du second axe : aucune case ne retrouvait sa déclinaison, et toute
                    //    matrice à un axe s'affichait ENTIÈREMENT VIDE — cases en pointillé, stock
                    //    invisible, sur la totalité d'un catalogue sans second axe.
                    const v = cellule.get(variantKey(c, size, null));
                    const pending = brouillon.codes[variantKey(c, size, null)];
                    // ⚠️ EN MODE SCAN, TOUTES LES CASES DEVIENNENT DES CHAMPS — pas seulement les vides (maquette :
                    //    `scanCell: scan`, sans condition) : on scanne une rangée entière à la douchette. Un code
                    //    différent sur une case connue la CORRIGE à l'enregistrement, sauf si le serveur l'a verrouillée.
                    if (scan) {
                      return (
                        <CaseScan
                          key={size}
                          cran={cran}
                          cellName={`${c} · ${size}`}
                          color={c}
                          existant={v}
                          pending={pending}
                          refused={scanError?.key === variantKey(c, size, null)}
                          onValidate={(code, next, stay) => scanCode({ color: c, size, size2: null }, code, next, stay)}
                        />
                      );
                    }
                    return <Case key={size} v={v} pending={pending} cran={cran} sheet={sheet} also={shared.get(variantKey(c, size, null)) ?? 0} />;
                  })}
                </div>
              ))}
            </div>

            {/* ⚠️ LE DÉFILEMENT SE DIT quand il existe : au-delà de six colonnes, des tailles sortent du
                cadre, et une matrice tronquée sans avertissement se lit comme une matrice complète. */}
            {sizes.length > 6 && (
              <p style={{ margin: '8px 0 0', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
                {t('{n} tailles — faites défiler horizontalement pour tout voir.', { n: sizes.length })}
              </p>
            )}
          </>
        )}

      {sheet && (
        <div style={{
          display: 'flex', alignItems: 'baseline', gap: 9, marginTop: 14, paddingTop: 12,
          borderTop: '1px solid var(--ink-200)', flexWrap: 'wrap',
        }}>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em',
            textTransform: 'uppercase', color: 'var(--ink-500)',
          }}>{t('Total en stock')}</span>
          <span style={{ fontSize: '18px', fontWeight: 800, letterSpacing: '-0.025em', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
            {totalStock}
          </span>
          <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
            {/* ⚠️ LE TOTAL NE DIT PAS TOUT : des déclinaisons jamais comptées y valent zéro, et un
                total qui les tairait se lirait comme un inventaire complet. DEUX PHRASES ENTIÈRES, le
                second compte passé en clé comptée : le moteur n'accorde qu'un `{n}` par phrase. */}
            {jamaisCompte > 0
              ? <Trans k='sur <1>{n}</1> déclinaison — dont {jamais}' vars={{ n: rows.length, jamais: t('{n} jamais comptée', { n: jamaisCompte }) }} parts={[(x) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</span>]} />
              : <Trans k='sur <1>{n}</1> déclinaison' vars={{ n: rows.length }} parts={[(x) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</span>]} />}
          </span>
        </div>
      )}

      {/* ⚠️ LA NOTE DE LA MAQUETTE, AMPUTÉE DE CE QUI N'EXISTE PAS : elle écrit « un clic sur la vignette ouvre le
          détail », et aucun détail n'existe derrière — nommer une commande absente est un mensonge d'écran (§23.0). */}
      {dimension && (vue === 'matrice' || !sheet) && (
        <p style={{ margin: sheet ? '10px 0 0' : '8px 0 0', fontSize: 'var(--t-meta)', color: sheet ? 'var(--ink-500)' : 'var(--ink-400)', lineHeight: 1.5 }}>
          {sheet
            ? t('Chaque vignette porte la quantité et les six derniers chiffres du gencode ; le code entier est dans l’infobulle.')
            : t('Chaque case porte la quantité ; le gencode est dans l’infobulle.')}
          {sheet && ' '}
          {sheet && <Trans k="La <1>Liste</1> le donne en entier." parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />}
          {sheet && ' '}
          {sheet && <Trans k="Les vignettes en <1>pointillé</1> sont les tailles de la grille non créées sur ce produit — le mode scan les active." parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />}
        </p>
      )}
      {!dimension && (vue === 'matrice' || !sheet) && (
      <p style={{ margin: sheet ? '10px 0 0' : '8px 0 0', fontSize: 'var(--t-meta)', color: sheet ? 'var(--ink-500)' : 'var(--ink-400)', lineHeight: 1.5 }}>
        {sheet
          ? (
            <>
              {t('Un clic sur le gencode le copie.')}
              {' '}
              <Trans k="Les vignettes en <1>pointillé</1> sont les tailles de la grille non créées sur ce produit — le mode scan les active." parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
              {' '}
              {/* ⚠️ LA PARTIE CONDITIONNELLE EST UNE PHRASE ENTIÈRE, pas un morceau recollé à la précédente :
                  « La » puis « Liste » puis « ajoute prix… » donnaient trois clés intraduisibles. */}
              <Trans k="La <1>Liste</1> ajoute prix et état par déclinaison." parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
            </>
          )
          : (
            <Trans
              k="Un clic sur le gencode le copie. Les vignettes en <1>pointillé</1> sont des déclinaisons qui n’existent pas ; « 0 » signale une réserve vide, et le gris clair une déclinaison jamais comptée."
              parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]}
            />
          )}
        {/* ⚠️ MÊME PIÈGE QUE DANS LA TÊTE : un ordre de tailles VIDE ne veut pas dire « aucune
            grille ». Le serveur le rend aussi bien sans grille que sur une grille NEUVE — cette note
            annonçait donc « aucune grille rattachée » trois lignes sous le nom de la grille. */}
        {gridSizes.length === 0 && <> {grid?.valeur
          ? t('La grille « {valeur} » ne déclare encore aucune taille : les colonnes suivent l’ordre d’apparition.', { valeur: grid.valeur })
          : t('Aucune grille de tailles n’est rattachée à ce modèle : les colonnes suivent l’ordre d’apparition.')}</>}
      </p>
      )}
    </div>
  );
}

/**
 * LA MATRICE À DEUX AXES — un bloc par couleur (fiche) ou une ligne par couleur × valeur (dépliage).
 *
 * ⚠️ DEUX ARRANGEMENTS DU DESSIN, UNE SEULE RÈGLE : la fiche montre la matrice comme sujet (un bloc par
 * couleur, la valeur de dimension en tête de ligne), le dépliage la glisse sous une rangée de tableau
 * (une ligne par couleur × valeur, la couleur sur la première, la valeur en pastille). Ce qui décide
 * — lignes, colonnes, états — vient du module pur ; ici on ne fait que dessiner.
 * ⚠️ LA LÉGENDE EST SOUS LA MATRICE, pas dans le pied de la fiche comme au dessin : le pied appartient
 * à l'hôte, qui ne sait pas quel onglet montre une matrice — la légende vit à côté de ce qu'elle explique.
 * ⚠️ LE SCAN N'EXISTE QU'EN FICHE (la barre d'outils n'est rendue que là) : sous une rangée de liste,
 * les cases restent des cases.
 */
function MatriceDeuxAxes({ sheet, cran2, dimension, rows, couleurs, sizes, valeurs, cellule, shared, pending, scan, scanErrorKey, onScan }: {
  sheet: boolean;
  cran2: Cran2;
  dimension: SecondDimension;
  rows: ArticleVariantSet['rows'];
  couleurs: readonly string[];
  sizes: readonly string[];
  valeurs: readonly string[];
  cellule: ReadonlyMap<string, ArticleVariantSet['rows'][number]>;
  /** Les déclinaisons qu'une case cache (`sharedCases`). */
  shared: ReadonlyMap<string, number>;
  /** Les codes en attente d'enregistrement, par case. */
  pending: VariantDraft['codes'];
  scan: boolean;
  /** La case du dernier scan refusé — son champ se borde de rouge. */
  scanErrorKey: string | null;
  /** Un code validé dans une case : l'onglet le range au brouillon, ou le refuse. */
  onScan: (color: string, size: string, size2: string, code: string, next: () => void, stay: () => void) => void;
}) {
  const colonnes = `${cran2.axe}px repeat(${sizes.length}, minmax(${cran2.w}px, ${cran2.fill}))`;
  /** Le coin nomme l'axe des lignes (« Longueur ↓ », le mot vient de la grille) ; les colonnes portent le mot du magasin pour la taille, suivi de « → ». */
  const enTete = (
    <>
      <span style={{ display: 'flex', alignItems: 'flex-end', padding: '0 8px 3px', fontFamily: 'var(--font-mono)', fontSize: '9.5px', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--violet-fg)' }}>
        {dimension.label} ↓
      </span>
      <span style={{ gridColumn: '2 / -1', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '9.5px', letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-500)', textAlign: 'center', paddingBottom: 2 }}>{`${fieldLabel('size')} →`}</span>
        <span style={{ display: 'grid', gridTemplateColumns: `repeat(${sizes.length}, minmax(${cran2.w}px, ${cran2.fill}))`, gap: cran2.gap }}>
          {sizes.map((size) => (
            <span key={size} style={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--r-xs)', background: 'var(--ink-100)', fontFamily: 'var(--font-mono)', fontSize: '11px', fontWeight: 700, color: 'var(--ink-700)' }}>{size}</span>
          ))}
        </span>
      </span>
    </>
  );
  /** Les cases d'une ligne (couleur × valeur), une par taille — en scan, des champs. */
  const cases = (c: string, l: string) => sizes.map((size) => {
    const v = cellule.get(variantKey(c, size, l));
    const enAttente = pending[variantKey(c, size, l)];
    if (scan) {
      return (
        <CaseScan
          key={size} cran={{ w: cran2.w, h: cran2.h }} cellName={[c, size, l].filter(Boolean).join(' · ')} color={c}
          existant={v} pending={enAttente} refused={scanErrorKey === variantKey(c, size, l)}
          // La case naît AVEC ses deux axes : c'est ce que le serveur écrit, séparément.
          onValidate={(code, next, stay) => onScan(c, size, l, code, next, stay)}
        />
      );
    }
    return <Case2 key={size} v={v} pending={enAttente} cran2={cran2} sheet={sheet} also={shared.get(variantKey(c, size, l)) ?? 0} />;
  });

  if (sheet) {
    return (
      <>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {couleurs.map((c) => {
            const total = blockTotal(rows.filter((v) => (v.color ?? SANS) === c));
            return (
              <div key={c}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
                  <span style={{ width: 9, height: 9, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)', flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: '13.5px', fontWeight: 700, color: 'var(--ink-900)' }}>{c}</span>
                  {/* ⚠️ Le total compte ce qui est MESURÉ, et dit à part ce qui ne l'a jamais été. */}
                  <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>
                    {t('{n} pièce', { n: total.pieces })}
                    {total.neverCounted > 0 && ` · ${t('{n} jamais comptée', { n: total.neverCounted })}`}
                  </span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ display: 'inline-grid', gridTemplateColumns: colonnes, gap: cran2.gap, minWidth: 0 }}>
                    {enTete}
                    {valeurs.map((l) => (
                      <Fragment key={l}>
                        <span style={{ height: cran2.h, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 'var(--r-xs)', background: 'var(--violet-bg)', fontFamily: 'var(--font-mono)', fontSize: '12px', fontWeight: 700, color: 'var(--violet-fg)' }}>{l}</span>
                        {cases(c, l)}
                      </Fragment>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <Legende2 sheet />
      </>
    );
  }
  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'inline-grid', gridTemplateColumns: colonnes, gap: cran2.gap, minWidth: 0 }}>
          {enTete}
          {couleurs.map((c) => valeurs.map((l, li) => (
            <Fragment key={`${c}|${l}`}>
              <span style={{ height: cran2.h, display: 'flex', alignItems: 'center', gap: 5, padding: '0 8px 0 2px', minWidth: 0 }}>
                {/* La couleur sur la PREMIÈRE ligne de son bloc seulement, comme au dessin. */}
                <span style={{ flex: 1, minWidth: 0, fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{li === 0 ? c : ''}</span>
                <span style={{ height: 18, display: 'inline-flex', alignItems: 'center', padding: '0 6px', borderRadius: 'var(--r-pill)', background: 'var(--violet-bg)', color: 'var(--violet-fg)', fontFamily: 'var(--font-mono)', fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>{l}</span>
              </span>
              {cases(c, l)}
            </Fragment>
          )))}
        </div>
      </div>
      <Legende2 sheet={false} />
    </>
  );
}

/**
 * UNE CASE DE LA MATRICE À DEUX AXES : la quantité, et en fiche les six derniers chiffres du code.
 * ⚠️ TROIS ÉTATS, ceux de la légende : en stock (teal), créée à zéro ou jamais comptée (blanche), non
 * créée (pointillé, un point). Le code ENTIER et l'état exact vivent dans l'infobulle.
 */
function Case2({ v, pending, cran2, sheet, also = 0 }: {
  v: ArticleVariantSet['rows'][number] | undefined; pending?: DraftCode; cran2: Cran2; sheet: boolean;
  /** Les déclinaisons de plus que cette case porte sans les montrer. */
  also?: number;
}) {
  const state = cellState(v);
  const socle: CSSProperties = {
    height: cran2.h, minWidth: cran2.w, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: 1, borderRadius: 'var(--r-xs)', boxSizing: 'border-box',
  };
  // ⚠️ UN CODE EN ATTENTE SE MONTRE COMME TEL : la case n'existera (ou ne changera) qu'à « Enregistrer ».
  if (pending) {
    return (
      <span
        title={`${fieldLabel('gencode')} ${pending.gencode} · ${t('à enregistrer')}`}
        style={{ ...socle, border: '1px solid var(--teal-400)', background: 'var(--paper)' }}
      >
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: 'var(--ink-800)' }}>…{pending.gencode.slice(-6)}</span>
        <span style={{ fontSize: '9px', fontWeight: 600, color: 'var(--teal-700)' }}>{t('à enregistrer')}</span>
      </span>
    );
  }
  if (!v || state === 'missing') {
    return (
      <span
        title={t('Non créée — le fournisseur ne la fait pas, ou « Scanner les gencodes » l’ouvrira')}
        style={{ ...socle, border: '1px dashed var(--ink-300)', background: 'var(--paper)', color: 'var(--ink-300)', fontSize: '13px', fontWeight: 700 }}
      >·</span>
    );
  }
  const q = v.stockQty ?? 0;
  const stocked = state === 'stocked';
  return (
    <span
      title={[
        v.gencode ? `${fieldLabel('gencode')} ${v.gencode}` : t('Sans gencode'),
        v.stockQty === null ? t('jamais comptée') : t('{v} en stock', { v: q }),
        v.isActive ? '' : t('retirée de la vente'),
      ].filter(Boolean).join(' · ')}
      style={{ ...socle, position: 'relative', border: `1px solid ${also > 0 ? 'var(--amber)' : stocked ? 'var(--teal-400)' : 'var(--ink-200)'}`, background: stocked ? 'var(--teal-100)' : 'var(--paper)' }}
    >
      <span style={{ fontSize: sheet ? '13px' : '12.5px', fontWeight: 700, color: stocked ? 'var(--teal-800)' : 'var(--ink-400)', fontVariantNumeric: 'tabular-nums' }}>{q}</span>
      {sheet && v.gencode && (
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '9px', color: 'var(--ink-400)' }}>…{v.gencode.slice(-6)}</span>
      )}
      <AlsoMark also={also} />
    </span>
  );
}

/**
 * La légende des trois états — les mots du dessin.
 * ⚠️ Deux mots pour le troisième état, comme au dessin : en fiche, le scan peut l'ouvrir ; sous une
 * rangée de liste personne ne scanne, alors on dit pourquoi elle manque.
 */
function Legende2({ sheet }: { sheet: boolean }) {
  const chip = (fond: string, trait: string): CSSProperties => ({ width: 13, height: 13, borderRadius: 4, background: fond, border: trait, flexShrink: 0 });
  const items: [CSSProperties, string][] = [
    [chip('var(--teal-100)', '1px solid var(--teal-400)'), 'en stock'],
    [chip('var(--paper)', '1px solid var(--ink-200)'), t('créée, stock 0')],
    [chip('var(--paper)', '1px dashed var(--ink-300)'), sheet ? t('non créée — un scan l’ouvrira') : t('non créée — le fournisseur ne la fait pas')],
  ];
  return (
    <div style={{ display: 'flex', gap: sheet ? 13 : 14, flexWrap: 'wrap', marginTop: 10, fontSize: '11.5px', color: 'var(--ink-600)' }}>
      {items.map(([s, mot]) => (
        <span key={mot} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={s} />{mot}</span>
      ))}
    </div>
  );
}

/**
 * LA VUE LISTE — « prix et état par déclinaison », ce que la matrice ne peut pas montrer.
 *
 * ⚠️ ELLE EXISTE PARCE QUE LES DEUX DONNÉES SONT DÉJÀ LÀ : `priceCents` et `isActive` sont servis par
 * la route des déclinaisons depuis le premier jour. Ce n'est donc pas une vue à remplir plus tard —
 * la bascule de la maquette montre du réel des deux côtés.
 * ⚠️ UNE DÉCLINAISON RETIRÉE RESTE VISIBLE, et le dit : la masquer ferait croire qu'elle n'existe pas,
 * alors qu'elle porte de l'historique de vente et un gencode qui ne se réattribue pas.
 * ⚠️ LES DÉCLINAISONS EN ATTENTE Y FIGURENT AUSSI (maquette : `declsOf` lit `fXCols` et `fEanOv`), marquées
 * « À enregistrer » — sans prix ni stock, qu'elles n'auront qu'une fois écrites.
 */
function VariantsList({ rows, pending = [], onDelete }: {
  rows: ArticleVariantSet['rows']; pending?: readonly DraftCode[];
  /** Supprimer une déclinaison — absent : pas de colonne de geste. */
  onDelete?: (gencode: string) => void;
}) {
  const corrections = new Map(pending.flatMap((p) => (p.from !== null ? [[p.from, p] as const] : [])));
  const created = pending.filter((p) => p.from === null);
  const aEnregistrer = <span style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--teal-700)' }}>{t('À enregistrer')}</span>;
  return (
    <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
      {/* Plancher relevé : l'en-tête « Prix de vente TTC » sans retour à la ligne est plus long que « Prix ». */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--t-body)', minWidth: 600 }}>
        <thead>
          <tr>
            {/* Clé React par POSITION : deux champs surchargés vers le même mot ne doivent pas se disputer une clé. */}
            {[fieldLabel('color'), fieldLabel('size'), fieldLabel('gencode'), fieldLabel('salePriceInclTax'), t('Stock'), t('État')].map((size, i) => (
              <th key={i} scope="col" style={{
                textAlign: i >= 3 && i <= 4 ? 'right' : 'left',
                padding: '0 10px 7px 0', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)',
                letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)', fontWeight: 600,
                borderBottom: '1px solid var(--ink-200)', whiteSpace: 'nowrap',
              }}>{size}</th>
            ))}
            {onDelete && <th scope="col" aria-label={t('Gestes')} style={{ borderBottom: '1px solid var(--ink-200)', width: 34 }} />}
          </tr>
        </thead>
        <tbody>
          {rows.map((v) => {
            const correction = v.gencode ? corrections.get(v.gencode) : undefined;
            return (
            <tr key={v.id} style={{ borderBottom: '1px solid var(--ink-100)' }}>
              <td style={{ padding: '8px 10px 8px 0', color: 'var(--ink-900)', fontWeight: 600 }}>{v.color ?? SANS}</td>
              {/* Le libellé INDUSTRIE quand la déclinaison a deux axes (« 40/32 ») — on stocke séparé, on affiche combiné. */}
              <td style={{ padding: '8px 10px 8px 0', fontFamily: 'var(--font-mono)', color: 'var(--ink-800)' }}>{industryLabel(v.size, v.size2)}</td>
              <td style={{ padding: '8px 10px 8px 0' }}>
                {correction
                  ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-700)' }}>{correction.gencode}</span>
                  : v.gencode
                  ? (
                    <Copyable value={v.gencode} label={t('Copier le gencode {gencode}', { gencode: v.gencode })} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-700)' }}>
                      {v.gencode}
                    </Copyable>
                  )
                  : <span style={{ color: 'var(--ink-400)' }}>{t('sans code')}</span>}
              </td>
              <td style={{ padding: '8px 10px 8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--ink-900)', whiteSpace: 'nowrap' }}>
                {formatMoney(v.priceCents, { thousandsSep: " " })}
              </td>
              <td style={{ padding: '8px 10px 8px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                <Stock qty={v.stockQty} />
              </td>
              <td style={{ padding: '8px 0' }}>
                {/* ⚠️ « Retiré » et non « Inactif » : c'est le mot du métier, et il dit ce qui a été fait
                    (on l'a sorti de la vente) plutôt qu'un état subi. */}
                {correction ? aEnregistrer : (
                  <span style={{
                    fontSize: 'var(--t-note)', fontWeight: 600,
                    color: v.isActive ? 'var(--ok-fg)' : 'var(--ink-500)',
                  }}>
                    {v.isActive ? t('Actif') : t('Retiré')}
                  </span>
                )}
              </td>
              {onDelete && (
                <td style={{ padding: '4px 0', textAlign: 'right' }}>
                  {/* ⚠️ SANS CODE-BARRES, RIEN À SUPPRIMER : aucune écriture n'atteint une telle ligne (reprise). */}
                  {v.gencode && (
                    <button type="button" className="btn-icon" onClick={() => onDelete(v.gencode!)}
                      aria-label={t('Supprimer la déclinaison {gencode}', { gencode: v.gencode })}
                      title={t('Supprimer la déclinaison {gencode}', { gencode: v.gencode })}
                      style={{ color: 'var(--ink-500)' }}><Icon name="trash" size={14} /></button>
                  )}
                </td>
              )}
            </tr>
            );
          })}
          {created.map((p) => (
            <tr key={`new-${p.gencode}`} style={{ borderBottom: '1px solid var(--ink-100)' }}>
              <td style={{ padding: '8px 10px 8px 0', color: 'var(--ink-900)', fontWeight: 600 }}>{p.color ?? SANS}</td>
              <td style={{ padding: '8px 10px 8px 0', fontFamily: 'var(--font-mono)', color: 'var(--ink-800)' }}>{industryLabel(p.size, p.size2)}</td>
              <td style={{ padding: '8px 10px 8px 0', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', color: 'var(--ink-700)' }}>{p.gencode}</td>
              <td style={{ padding: '8px 10px 8px 0', textAlign: 'right', color: 'var(--ink-400)' }}>—</td>
              <td style={{ padding: '8px 10px 8px 0', textAlign: 'right', color: 'var(--ink-400)' }}>—</td>
              <td style={{ padding: '8px 0' }}>{aEnregistrer}</td>
              {onDelete && <td />}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Ce qui interdit de corriger le code-barres — en DEUX longueurs.
 * ⚠️ LE MOTIF SE DIT, il ne se devine pas : un champ grisé sans explication fait chercher une panne
 * là où il y a une RÈGLE, et l’opérateur réessaie (§23.0).
 * ⚠️ `court` TIENT DANS LA TUILE (128 px) : une phrase complète y débordait. `long` reste dans
 * l’infobulle — raccourcir ne doit pas AMPUTER l’explication, seulement la déplacer.
 */
const LOCK_REASON: Record<string, { court: Translatable; long: Translatable }> = {
  vendu: { court: key('déjà vendue'), long: key('déjà vendue — une pièce fiscale porte ce code') },
  sorti: { court: key('déjà sortie'), long: key('une quantité est déjà sortie du stock sous ce code') },
  // ⚠️ « AJUSTÉ », PAS « INVENTORIÉ » (revue 2026-08-25). Ce motif couvre TOUT ajustement de stock, et
  //    la caisse en produit trois — inventaire, correction manuelle et CASSE (`StockScreen`). Annoncer
  //    « un inventaire a été clôturé » à qui vient de saisir une casse est faux, et l'onglet Mouvements
  //    de la même fiche dit « Casse ou perte » au même instant. Le verrou, lui, est le bon : un stock
  //    ajusté a statué ce code, quelle qu'en soit la raison.
  inventorie: { court: key('stock ajusté'), long: key('un ajustement de stock (inventaire, correction ou casse) a statué ce code') },
};

/**
 * UNE VIGNETTE EN MODE SCAN — un champ, prérempli du code de la case (en attente, sinon en base).
 *
 * ⚠️ « ENTRÉE » RANGE LE CODE PUIS PASSE À LA SUIVANTE, et c'est tout le geste : une douchette envoie le
 * code puis un retour chariot. Sans ce passage automatique, l'opérateur devrait lâcher la douchette
 * pour cliquer la case d'après — à douze tailles, c'est le mode qui perd son intérêt.
 * ⚠️ RIEN N'EST ÉCRIT AU SCAN, comme au dessin : le code va au brouillon de la fiche, et part avec « Enregistrer ».
 *    Un code déjà porté sur l'article est refusé tout de suite ; porté par un autre article, à l'enregistrement.
 */
function CaseScan({ cran, cellName, color, existant, pending, refused, onValidate }: {
  cran: { w: number; h: number };
  /**
   * La case telle que l'opérateur la lit (coloris, taille, second axe) — elle NOMME le champ.
   * ⚠️ La grille rend une case par déclinaison : un nom sans elle se répétait sur toute la grille, et une aide technique
   *    annonçait « code-barres de cette déclinaison » sans dire laquelle.
   */
  cellName: string;
  /** La couleur de la case : c'est par elle qu'on retrouve la première case d'une couleur qu'on vient d'ajouter. */
  color: string;
  /** La déclinaison déjà connue, s’il y en a une. */
  existant?: ArticleVariantSet['rows'][number];
  /** Le code en attente pour cette case, s'il y en a un. */
  pending?: DraftCode;
  /** Le dernier scan de cette case a été refusé : son champ se borde de rouge, comme au dessin. */
  refused: boolean;
  onValidate: (code: string, next: () => void, stay: () => void) => void;
}) {
  const initial = pending?.gencode ?? existant?.gencode ?? '';
  const [saisi, setSaisi] = useState(initial);
  // ⚠️ LE CHAMP SUIT LE BROUILLON : « Annuler » au pied de la fiche le vide, et la case doit revenir à la base.
  useEffect(() => { setSaisi(initial); }, [initial]);
  // ⚠️ LE VERROU VIENT DU SERVEUR, jamais d’une règle rejouée ici : une garde qui ne vit que dans le
  // navigateur n’est pas une garde, et deux versions de la même règle divergeraient (forme A du
  // catalogue des défauts). L’écran ne fait qu’AFFICHER ce que le serveur a déjà décidé.
  const verrou = existant?.renameLock ?? null;
  // ⚠️ UNE DÉCLINAISON SANS CODE NE SE CORRIGE PAS ICI : la correction part du code actuel, et elle n'en a pas.
  const inerte = !!verrou || (!!existant && !existant.gencode);
  const tile = (
    <div style={{
      width: cran.w, height: cran.h, flexShrink: 0, borderRadius: 'var(--r-ctrl)',
      border: `1.5px solid ${inerte ? 'var(--ink-200)' : 'var(--teal-400)'}`, background: 'var(--paper)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
      boxSizing: 'border-box',
    }}>
      <input
        value={saisi}
        onChange={(e) => setSaisi(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          // ⚠️ LES BLANCS TOMBENT (maquette : `replace(/\s+/g, '')`) : une douchette en glisse parfois un.
          // ⚠️ UNE VIGNETTE LAISSÉE TELLE QUELLE EST IGNORÉE — c'est ce qui permet de sauter une taille qu'on
          //    ne reçoit pas sans quitter le mode ; vidée, elle revient à ce que la base porte.
          const code = saisi.replace(/\s+/g, '');
          const champ = e.currentTarget;
          onValidate(code, () => {
            // Le champ suivant DANS L'ORDRE DU DOM : la rangée se remplit de gauche à droite.
            const tous = Array.from(document.querySelectorAll<HTMLInputElement>('input[data-scan]'));
            const i = tous.indexOf(champ);
            tous[i + 1]?.focus();
            tous[i + 1]?.select();
          }, () => { champ.focus(); champ.select(); });
        }}
        data-scan=""
        data-scan-color={color}
        inputMode="numeric"
        disabled={inerte}
        placeholder={verrou ? t('verrouillé') : inerte ? t('sans code') : t('scanner…')}
        aria-label={t('{v} de la déclinaison {declinaison}', { v: fieldLabel('gencode'), declinaison: cellName })}
        aria-invalid={refused || undefined}
        style={{
          width: cran.w - 16, height: 24, borderRadius: 'var(--r-xs)',
          fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', textAlign: 'center', outline: 'none',
          // ⚠️ UN CHAMP INERTE DOIT EN AVOIR L’AIR. `disabled` seul ne se voit pas : deux cases
          // identiques à l’œil auraient des comportements opposés, et l’opérateur ne l’apprendrait
          // qu’en tapant dans le vide. Le curseur le dit aussi, pour qui survole avant de cliquer.
          border: `1.5px solid ${refused ? 'var(--danger)' : inerte ? 'var(--ink-100)' : 'var(--ink-200)'}`,
          background: inerte ? 'var(--ink-100)' : 'var(--ink-50)',
          color: inerte ? 'var(--ink-400)' : 'var(--ink-900)',
          cursor: inerte ? 'not-allowed' : 'text',
        }}
      />
      {/* ⚠️ Le libellé dit l’ÉTAT RÉEL de la case (maquette : `c.qL`) — ce qu’elle porte en stock, qu'elle attend
          l'enregistrement, ou qu’elle n’existe pas ; un verrou se dit en rouge, sinon le champ inerte resterait muet. */}
      {verrou
        ? <Badge tone="danger" size="xs">{LOCK_REASON[verrou] ? t(LOCK_REASON[verrou].court) : verrou}</Badge>
        : (
          <span style={{ fontSize: cran.w >= 128 ? '10px' : '9px', color: pending ? 'var(--teal-700)' : 'var(--ink-500)', fontWeight: pending ? 600 : 400 }}>
            {pending
              ? t('à enregistrer')
              : existant ? (existant.stockQty == null ? t('jamais comptée') : t('{v} en stock', { v: existant.stockQty })) : t('non créée')}
          </span>
        )}
    </div>
  );

  // ⚠️ L’EXPLICATION ENVELOPPE TOUTE LA TUILE, pas seulement la pastille : le geste naturel est de
  // cliquer dans le champ inerte, et c’est là qu’on veut savoir pourquoi il ne prend rien. Un champ
  // `disabled` n’émet aucun événement de survol — seule l’enveloppe peut l’entendre à sa place.
  // ⚠️ ET PLUS `title` : l’infobulle du navigateur est ignorée sur un champ désactivé, donc la raison
  // du verrou n’était lisible nulle part exactement là où elle sert.
  return verrou
    ? <Tooltip label={t('code-barres verrouillé')} content={LOCK_REASON[verrou] ? t(LOCK_REASON[verrou].long) : verrou} style={{ flexShrink: 0 }}>{tile}</Tooltip>
    : tile;
}

/**
 * UNE CASE DE LA MATRICE À UN AXE : le gencode au-dessus, le stock en dessous — ou « non créée » en pointillé.
 * ⚠️ UN CODE EN ATTENTE SE MONTRE COMME TEL (« à enregistrer ») : la case n'existera, ou ne changera, qu'à
 *    l'enregistrement de la fiche. Le dessin la peint déjà créée à « 0 en stock », ce qu'elle n'est pas encore.
 */
function Case({ v, pending, cran, sheet, also = 0 }: {
  v: ArticleVariantSet['rows'][number] | undefined;
  pending?: DraftCode;
  cran: { w: number; h: number };
  /** La fiche, où le scan crée la case ; sous une rangée de liste, une case absente n'est qu'une absence. */
  sheet: boolean;
  /** Les déclinaisons de plus que cette case porte sans les montrer. */
  also?: number;
}) {
  const socle: CSSProperties = {
    width: cran.w, height: cran.h, flexShrink: 0, borderRadius: 'var(--r-ctrl)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
  };
  if (pending) {
    return (
      <div
        title={`${fieldLabel('gencode')} ${pending.gencode} · ${t('à enregistrer')}`}
        style={{ ...socle, boxSizing: 'border-box', border: '1.5px solid var(--teal-400)', background: 'var(--paper)' }}
      >
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: cran.w >= 128 ? '10.5px' : '9.5px', fontWeight: 600, letterSpacing: '.01em', color: 'var(--ink-800)' }}>
          {cran.w >= 128 ? pending.gencode : `…${pending.gencode.slice(-6)}`}
        </span>
        <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--teal-700)' }}>{t('à enregistrer')}</span>
      </div>
    );
  }
  if (!v && !sheet) {
    return (
      <div
        title={t('Cette déclinaison n’existe pas.')}
        style={{ ...socle, border: '1.5px dashed var(--ink-200)', background: 'transparent' }}
      />
    );
  }
  if (!v) {
    // ⚠️ LA VIGNETTE « NON CRÉÉE » DU DESSIN (`ghostCell`) : pointillé, fond tramé, le mot — et l'infobulle dit
    //    comment la créer.
    return (
      <div
        title={t('Déclinaison non créée dans la grille de tailles — « Scanner les gencodes » pour la créer')}
        style={{ ...socle, boxSizing: 'border-box', border: '1.5px dashed var(--ink-200)', background: 'var(--ink-50)' }}
      >
        <span style={{ fontSize: '10.5px', fontWeight: 600, color: 'var(--ink-400)' }}>{t('non créée')}</span>
      </div>
    );
  }
  return (
    <div
      style={{
        ...socle,
        position: 'relative',
        border: `1.5px solid ${also > 0 ? 'var(--amber)' : v.isActive ? 'var(--ink-200)' : 'var(--ink-100)'}`,
        background: v.isActive ? 'var(--paper)' : 'var(--ink-50)',
      }}
      title={[
        v.isActive ? '' : t('Déclinaison retirée de la vente.'),
        v.stockQty === null ? t('Aucun mouvement de stock n’a jamais été enregistré pour cette déclinaison.') : '',
      ].filter(Boolean).join(' ') || undefined}
    >
      {/* ⚠️ LE GENCODE EN PREMIER, LE STOCK EN DESSOUS — l'ordre de la maquette (relevé l.3606-3612),
          que j'avais inversé. Ce n'est pas une préférence : la vignette IDENTIFIE d'abord (« quel
          code-barres ? ») puis QUANTIFIE. Mettre le stock en tête fait lire la matrice comme un
          inventaire, alors qu'elle sert à retrouver un code à scanner.
          ⚠️ ET LE GENCODE ENTIER en fiche : tronqué à six chiffres il ne sert plus à rien de ce qu'on
          en fait — le lire à voix haute, le comparer à une étiquette. Le dépliage, lui, reste étroit. */}
      {v.gencode
        ? (
          <Copyable
            value={v.gencode}
            label={t('Copier le gencode {gencode}', { gencode: v.gencode })}
            style={{ fontFamily: 'var(--font-mono)', fontSize: cran.w >= 128 ? '10.5px' : '9.5px', fontWeight: 600, letterSpacing: '.01em', color: 'var(--ink-800)' }}
          >
            {cran.w >= 128 ? v.gencode : `…${v.gencode.slice(-6)}`}
          </Copyable>
        )
        : <span style={{ fontSize: '9.5px', color: 'var(--ink-300)' }}>{t('sans code')}</span>}
      {/* ⚠️ « N en stock » et non un nombre nu : dans une vignette qui porte déjà un code à 13 chiffres,
          un « 6 » isolé se lit comme la suite du code. La maquette l'écrit en toutes lettres. */}
      <span style={{
        fontSize: '11px', fontWeight: v.stockQty ? 700 : 600,
        color: !v.isActive || v.stockQty === null ? 'var(--ink-300)' : v.stockQty ? 'var(--ink-900)' : 'var(--ink-300)',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {t('{v} en stock', { v: v.stockQty ?? 0 })}
      </span>
      <AlsoMark also={also} />
    </div>
  );
}

/** LA MARQUE D'UNE CASE QUI CACHE DES DÉCLINAISONS — « +1 » en coin, et l'infobulle dit où les voir. */
function AlsoMark({ also }: { also: number }) {
  if (also <= 0) return null;
  return (
    <span
      title={t('Cette case porte {declinaisons} de plus — la vue Liste les montre toutes.', { declinaisons: t('{n} déclinaison', { n: also }) })}
      style={{
        position: 'absolute', top: -7, right: -7, minWidth: 18, height: 18, padding: '0 5px', boxSizing: 'border-box',
        borderRadius: 'var(--r-pill)', background: 'var(--amber)', color: 'var(--ink-900)', fontSize: '10.5px', fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums',
      }}
    >+{also}</span>
  );
}

/**
 * ⚠️ LE COMPOSANT `Mouvements` A ÉTÉ SUPPRIMÉ (audit 2026-08-25) — 105 lignes exportées que RIEN
 * n'importait. Son en-tête annonçait un « onglet Mouvements » qui n'existe pas dans `ONGLETS` : la
 * fiche rend `EtatStock` sous l'onglet Stock. La mécanique du grand livre, elle, est bien vivante —
 * `CompositionStock` (plus bas) appelle `api.articleMovements` pour UNE déclinaison, au clic sur une
 * case. Du code maintenu — libellés, tons, troncature — qui n'atteignait aucun écran, et qui donnait
 * à chaque relecture l'illusion qu'un onglet restait à brancher.
 */

/**
 * Les trois mesures de la maquette, dans son ordre et avec SES couleurs.
 * ⚠️ Valeurs relevées sur le dessin (l. 3915-3990), pas approchées : `--blue-fg` pour les entrées,
 * `oklch(0.45 0.15 25)` pour les ventes, `--teal-800` pour le stock. Une teinte voisine se voit.
 */
/**
 * Les trois mesures de l'onglet Stock — TONS RELEVÉS de la maquette (`fStkRows`), y compris l'état
 * ZÉRO : une barre à zéro s'ASSOURDIT (fond `ink-50`, chiffre `ink-300`/`ink-400`) au lieu de porter
 * sa couleur pleine — c'est ce qui fait ressortir d'un coup d'œil les cases où il s'est passé quelque
 * chose. `legende` = les tons du carré de légende (bordure au TON MOYEN, pas au ton d'encre).
 */
const MESURES = [
  { key: 'E', label: key('entrées'), fond: 'var(--blue-bg)', trait: 'var(--blue-fg)', legende: 'var(--blue)', muetFg: 'var(--ink-300)' },
  { key: 'V', label: key('ventes'), fond: 'oklch(0.95 0.04 25)', trait: 'oklch(0.45 0.15 25)', legende: 'oklch(0.72 0.13 25)', muetFg: 'var(--ink-300)' },
  { key: 'S', label: key('stock'), fond: 'var(--teal-100)', trait: 'var(--teal-800)', legende: 'var(--teal-400)', muetFg: 'var(--ink-400)' },
] as const;

/** Fond assourdi commun des barres à zéro (relevé maquette). */
const BARRE_MUETTE_BG = 'var(--ink-50)';

const MONO = 'var(--font-mono)';

/** Date courte pour les lignes de composition. */
const shortDay = (ms: number): string => {
  if (!ms) return '—';
  const d = new Date(ms);
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${String(d.getFullYear()).slice(2)}`;
};

/**
 * LA COMPOSITION DU STOCK D'UNE DÉCLINAISON — ce que le clic sur une case ouvre.
 *
 * ⚠️ TROIS COLONNES, comme le dessin : entrées · sorties · le stock en gros. Le grand livre
 * chronologique existe (`articleMovements`) mais n'a PAS d'écran à lui : c'est sa mécanique qui sert
 * ici, filtrée sur une seule déclinaison. Deux lecteurs pour deux périmètres du même livre finiraient
 * par ne plus dire la même chose.
 *
 * ⚠️ LA SOMME AFFICHÉE SOUS LE STOCK EST CELLE DES LIGNES MONTRÉES, et elle peut ne pas retomber sur
 * le stock : les données de reprise portent des écritures de bouclage. On montre les deux plutôt que
 * d'en cacher une — c'est précisément ce qu'une « composition » doit permettre de constater.
 */
/**
 * LE NOM FRANÇAIS D'UN MOUVEMENT DE STOCK.
 *
 * ⚠️ ON AFFICHAIT LE CODE DE STOCKAGE BRUT — « Sale », « Other » à l'écran d'un commerçant français.
 * La convention est pourtant nette : le CODE en anglais, l'UI en français. Un code de base n'est pas
 * un libellé : il est stable, technique, et ne se traduit pas tout seul en descendant vers l'écran.
 *
 * ⚠️ UN CODE INCONNU NE SE DÉGUISE PAS. Il rend « Mouvement de stock » — vrai et neutre — et le code
 * réel part dans la ligne de provenance. Lui inventer un beau nom français ferait passer un trou de
 * notre table de correspondance pour une nature de mouvement établie (§23.0).
 */
/**
 * Cette ligne est-elle une SORTIE DE VENTE — la seule qui puisse porter un lien vers une pièce ?
 * ⚠️ ELLE SE DÉDUIT DU LIBELLÉ, jamais d'un second test sur `reason` : la qualification d'un mouvement
 * a déjà une règle (`libelleDeMouvement`), et en écrire une seconde garantirait qu'elles divergent.
 */
export const estVente = (m: StockMovementRow, entry: boolean): boolean =>
  movementLabel(m, entry) === 'Vente en caisse';

export function movementLabel(m: StockMovementRow, entry: boolean): string {
  const q = `${m.reason ?? ''} ${m.type}`.toLowerCase();
  // ⚠️ AVANT le motif générique « retour » : une ENTRÉE `return_supplier` est une ANNULATION de retour
  // fournisseur (la marchandise revient au stock, l'achat est rétabli) — l'afficher « Retour client »
  // contredirait la valorisation, qui la repondère à juste titre (revue du 2026-08-23, volumétrie :
  // 1 ligne sur SLIDERZ, note « retour du 19.12 sortie de l'article 2x »).
  if (/return_supplier/.test(q)) return entry ? t('Annulation de retour fournisseur') : t('Retour fournisseur');
  if (/retour|return/.test(q)) return entry ? t('Retour client') : t('Retour fournisseur');
  // ⚠️ Une ENTRÉE au motif vente est un RETOUR CLIENT (164 lignes reprises, liées à leur ticket) —
  // « Vente en caisse » avec un +1 contredisait le sens du mouvement (correctif 2026-08-24).
  if (/vente|sale|ticket/.test(q)) return entry ? t('Retour client') : t('Vente en caisse');
  if (/reception|réception|achat|livraison/.test(q)) return t('Réception fournisseur');
  // L'écriture de bouclage AVANT la règle « reprise » : un ajustement de reprise n'est pas une entrée initiale.
  if (m.type === 'adjustment' && /reprise|bouclage/.test(q)) return t('Écriture de bouclage');
  if (/initial|reprise|opening/.test(q)) return t('Entrée initiale');
  if (/transfert|transfer|mutation|retroces/.test(q)) return entry ? t('Rétrocession reçue') : t('Rétrocession envoyée');
  if (/casse|breakage|perte|loss|destruc/.test(q)) return t('Casse ou perte');
  if (/theft|vol\b/.test(q)) return t('Vol · démarque inconnue');
  if (/gift|cadeau|offert/.test(q)) return t('Cadeau · geste commercial');
  if (/bouclage/.test(q)) return t('Écriture de bouclage');
  if (/inventaire|inventory/.test(q)) return entry ? t('Inventaire — surplus') : t('Inventaire — démarque');
  if (/manual_correction|correction|ajust|adjust|regul/.test(q)) return t('Correction manuelle');
  // ⚠️ NATURE NON QUALIFIÉE mais ACQUISITION DOCUMENTÉE (demande utilisateur 2026-08-24 : « mouvement
  // de stock, c'est trop vague ») : la reprise a laissé le motif vide (`other`) sur ~19 000 entrées,
  // or la ligne porte une ENTRÉE VALORISÉE — n° de BL, origine, fournisseur — la MÊME preuve que la
  // valorisation utilise déjà pour repondérer le PMP. On lit cette preuve ; on n'invente rien : sans
  // elle, le libellé reste générique et le code réel part en provenance.
  if (entry && (m.sourceRef || m.origin || m.supplierId)) return t('Réception fournisseur');
  return t('Mouvement de stock');
}

/**
 * D'OÙ VIENT CE MOUVEMENT — la ligne que le commerçant lit pour rapprocher d'un bon papier.
 *
 * ⚠️ RÉPOND À UNE QUESTION QUI SE POSAIT SANS RÉPONSE : « cette entrée vient-elle d'un BL ? ». La
 * référence vit dans `purchase_entry`, pas sur le mouvement — jusqu'ici l'écran ne la montrait pas,
 * donc une réception ne se vérifiait pas.
 * ⚠️ L'ABSENCE DE DOCUMENT SE DIT. Un lien manquant est FRÉQUENT et attendu sur les données reprises
 * (le logiciel précédent ne l'avait pas) : le taire laisserait croire à une lacune de notre part,
 * l'inventer serait pire.
 */
export function movementSource(m: StockMovementRow, entry: boolean): string {
  const date = shortDay(m.at);
  const bouts: string[] = [];

  if (m.sourceRef) bouts.push(m.sourceRef.startsWith('BL') ? m.sourceRef : t('BL {v}', { v: m.sourceRef }));
  // ⚠️ LA REPRISE ÉCRIT `reprise_entree` / `reprise_historique_*`, PAS `legacy` (constaté 2026-08-23 :
  // la mention ne s'affichait JAMAIS — le test utilisait la valeur documentée, la base porte l'autre).
  // Les deux formes restent reconnues : le code se lit dans les deux vocabulaires selon l'époque.
  if (m.origin === 'legacy' || m.origin?.startsWith('reprise')) bouts.push(t('reprise du logiciel précédent'));
  else if (m.origin === 'edi') bouts.push(t('reçu par EDI'));
  else if (m.origin === 'manual') bouts.push('saisie manuelle');
  else if (entry && /reception|réception|achat|livraison/i.test(`${m.reason ?? ''} ${m.type}`)) {
    // ⚠️ Seulement sur une RÉCEPTION : une vente ou un inventaire n'ont pas de bon de livraison, et
    // leur en réclamer un ferait passer une normalité pour un manque.
    bouts.push(t('aucun document rattaché'));
  }
  if (m.note) bouts.push(m.note);
  // Le code réel n'apparaît QUE si on n'a pas su le nommer — c'est ce qui rend le trou signalable.
  if (movementLabel(m, entry) === 'Mouvement de stock') bouts.push(t('code « {v} »', { v: m.reason ?? m.type }));

  return bouts.length ? `${date} · ${bouts.join(' · ')}` : date;
}

/** Combien de mouvements une colonne montre avant de résumer le reste — au-delà, le panneau chasse la grille. */
const VISIBLE_MOVEMENTS = 8;

/**
 * Le dessin qui va avec un mouvement de stock.
 *
 * ⚠️ LE TYPE SE LIT EN DIAGONALE OU PAS DU TOUT. Réception, vente, retour et régularisation portaient
 * le même bloc de texte : sur une colonne de huit lignes, plus personne ne distinguait un retour
 * client d'une entrée fournisseur. La pastille rend ça visible sans rien lire.
 * ⚠️ REPLI SUR LE SENS, jamais sur rien : un type inconnu reste une entrée ou une sortie, ce qui est
 * toujours vrai — une pastille vide laisserait croire à un mouvement d'une autre nature.
 */
function movementIcon(m: { type: string; reason: string | null }, entry: boolean): IconName {
  const q = `${m.reason ?? ''} ${m.type}`.toLowerCase();
  if (/retour|return/.test(q)) return 'returnBox';
  if (/vente|sale|ticket/.test(q)) return 'bag';
  if (/transfert|transfer|mutation|retroces/.test(q)) return 'swap';
  if (/casse|perte|loss|destruc|theft|vol\b/.test(q)) return 'trash';
  // ⚠️ L'INVENTAIRE PORTE LA CROIX (relevé maquette + décision utilisateur 2026-08-24) : un écart
  // d'inventaire est un PROBLÈME constaté, et la croix le dit d'un coup d'œil — la flèche, elle,
  // revient à la CORRECTION, qui est un geste dont le sens (entrée/sortie) est l'information utile.
  // Le motif anglais `inventory` manquait au test : la démarque retombait sur la flèche par défaut.
  if (/inventaire|inventory/.test(q)) return 'x';
  if (/regul|ajust|adjust|correction/.test(q)) return entry ? 'inbound' : 'outbound';
  if (/ecart|écart|bouclage/.test(q)) return 'sliders';
  if (/reception|réception|entry|achat|livraison/.test(q)) return 'inbound';
  return entry ? 'inbound' : 'outbound';
}

function CompositionStock({ modelKey, cell, onClose }: {
  modelKey: string;
  cell: StockCell;
  onClose: () => void;
}) {
  const [jeu, setJeu] = useState<StockMovementSet | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const nav = useNavigate();

  useEffect(() => {
    let vivant = true;
    setJeu(null); setFailure(null);
    api.articleMovements(modelKey, cell.gencode)
      .then((r) => { if (vivant) setJeu(r); })
      .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [modelKey, cell.gencode]);

  // « Blanc · taille M » — le mot « taille » est du RELEVÉ (`fStkSelTitle`), pas un ornement : sans
  // lui, un coloris et une taille numérique se lisent comme deux codes accolés (« Brut · 42 »).
  const heading = [cell.color, cell.size ? `${fieldLabel('size').toLowerCase()} ${cell.size}` : null].filter(Boolean).join(' · ') || t('Déclinaison');
  const entrees = (jeu?.rows ?? []).filter((m) => m.qty > 0);
  const sorties = (jeu?.rows ?? []).filter((m) => m.qty < 0);
  const sum = (jeu?.rows ?? []).reduce((n, m) => n + m.qty, 0);
  const totalEntrees = entrees.reduce((n, m) => n + Math.abs(m.qty), 0);
  const totalSorties = sorties.reduce((n, m) => n + Math.abs(m.qty), 0);

  // ⚠️ RELEVÉ DU DESSIN, PAS DÉDUIT DU THÈME. La maquette donne `teal-700 #117a64` aux entrées et deux
  // teintes oklch aux sorties ; aucune n'existe en jeton chez nous (`--teal-700` n'est pas défini, seuls
  // 400/500/800 le sont). Les poser en littéral AVEC leur origine vaut mieux qu'un jeton approchant :
  // une colonne de chiffres décalée d'un ton se lit comme une erreur de donnée.
  const ENTREE = { trait: 'var(--teal-700)', fond: 'var(--teal-100)', encre: 'var(--teal-800)' };
  const SORTIE = { trait: 'oklch(0.45 0.15 25)', fond: 'oklch(0.95 0.04 25)', encre: 'oklch(0.45 0.15 25)' };

  const column = (columnHeading: string, rows: typeof entrees, bord: boolean, entry: boolean) => {
    const tone = entry ? ENTREE : SORTIE;
    const total = rows.reduce((n, m) => n + Math.abs(m.qty), 0);
    return (
      <div style={{ padding: '13px 16px', borderRight: bord ? '1px solid var(--ink-100)' : undefined, minWidth: 0 }}>
        {/* ⚠️ `marginBottom: 7` — le titre de colonne respire AVANT la première ligne, qui porte déjà
            son propre filet. Collés, le libellé se lit comme l'en-tête de la ligne du dessous. */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 9, marginBottom: 7 }}>
          <span style={{ fontFamily: MONO, fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{columnHeading}</span>
          {/* ⚠️ LE TOTAL PORTE LA COULEUR DE SA COLONNE. En encre neutre, entrées et sorties se
              confondaient : c'est la teinte qui dit le SENS du mouvement, pas le libellé.
              ⚠️ ZÉRO SANS SIGNE : « −0 » n'a aucun sens, zéro n'est ni négatif ni positif. */}
          <span style={{ fontFamily: MONO, fontSize: 'var(--t-body)', fontWeight: 800, color: tone.trait, fontVariantNumeric: 'tabular-nums' }}>
            {total === 0 ? '0' : `${entry ? '+' : '−'}${total}`}
          </span>
        </div>
        {rows.length === 0 ? (
          // ⚠️ L'ABSENCE SE DIT, et elle se dit AVEC SON MOTIF : « aucune » tout court laisse croire à
          // une lecture partielle. Le dessin écrit la phrase entière.
          <div style={{ padding: '10px 0', borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}>
            {entry ? t('Aucune entrée — rien reçu sur cette déclinaison.') : t('Aucune sortie — rien vendu, aucun écart.')}
          </div>
        ) : rows.slice(0, VISIBLE_MOVEMENTS).map((m) => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 0', borderTop: '1px solid var(--ink-100)' }}>
            {/* ⚠️ UNE PASTILLE PAR MOUVEMENT, et son dessin dépend du TYPE : réception, vente, retour et
                régularisation ne se distinguaient que par un libellé qu'on ne lit pas en diagonale. */}
            <span style={{
              width: 24, height: 24, borderRadius: 'var(--r-xs)', background: tone.fond, color: tone.encre,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Icon name={movementIcon(m, entry)} size={12} stroke={2.2} />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 7, minWidth: 0 }}>
                <span style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{movementLabel(m, entry)}</span>
                {/* LA PIÈCE DERRIÈRE LE MOUVEMENT (demande utilisateur 2026-08-24) : le numéro se lit,
                    le clic ouvre la vente dans le journal.
                    ⚠️ SON ABSENCE SE DIT (2026-08-26). Elle ne se voyait pas : une sortie de vente
                    reprise n'affichait rien, et on cherchait un lien qui n'existait pas.
                    ⚠️ ET L'EXPLICATION QU'ON EN DONNAIT ÉTAIT FAUSSE. Ce commentaire affirmait que le
                    legacy ne portait pas ce lien — vérification faite dans la source : `Sorti_st`
                    renseigne `IDVentes` ET `IDArticle_vendu` sur ses 23 653 lignes, SANS UN TROU.
                    C'est la reprise qui les jetait. Elle les porte désormais (`sale_line_id`), donc ce
                    message ne concerne plus que deux cas RÉELS : un magasin repris avant ce correctif
                    (à reprendre), et les 1 268 sorties dont la source a elle-même perdu la ligne. */}
                {m.saleId && m.saleNumber ? (
                  <button
                    type="button"
                    title={t('Ouvrir cette vente dans le journal')}
                    onClick={() => nav(`/journal?open=${encodeURIComponent(m.saleId!)}`)}
                    style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontFamily: MONO, fontSize: 'var(--t-meta)', color: tone.encre, textDecoration: 'underline', flexShrink: 0 }}
                  >{m.saleNumber}</button>
                ) : estVente(m, entry) ? (
                  <span
                    title={t('Aucune vente n’est rattachée à cette sortie : soit le magasin a été repris avant que le lien ne soit conservé (une nouvelle reprise le rétablira), soit l’ancien logiciel pointait lui-même une ligne de vente qu’il avait perdue.')}
                    style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', flexShrink: 0, whiteSpace: 'nowrap' }}
                  >{t('vente non reliée')}</span>
                ) : null}
              </span>
              {/* ⚠️ Coupé net plutôt que replié : une note longue pousserait la quantité hors de la
                  colonne, et c'est elle qu'on vient lire. */}
              <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {movementSource(m, entry)}
              </span>
            </span>
            <span style={{ fontFamily: MONO, fontSize: 'var(--t-lead)', fontWeight: 800, color: tone.trait, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
              {entry ? '+' : '−'}{Math.abs(m.qty)}
            </span>
          </div>
        ))}
        {rows.length > VISIBLE_MOVEMENTS && (
          // ⚠️ LE RESTE EST ANNONCÉ, jamais escamoté : une liste coupée en silence se lit comme une
          // liste complète, et le total d'en haut paraîtrait alors faux.
          <div style={{ padding: '6px 0 0', borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>
            {t('+ {n} autre — le total ci-dessus les compte.', { n: rows.length - VISIBLE_MOVEMENTS })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      marginTop: 14, border: '1.5px solid var(--teal-300)', borderRadius: 'var(--r-md)',
      overflow: 'hidden', background: 'var(--paper)',
      // ⚠️ APPARITION RELEVÉE (maquette : `pop .18s`). Elle ne SIMULE aucun travail — le panneau
      // est déjà là quand elle joue : elle dit seulement d'où vient ce qui vient de s'ouvrir.
      animation: 'pop .18s cubic-bezier(.2,.7,.3,1)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 11, padding: '11px 16px',
        background: 'var(--teal-100)', borderBottom: '1px solid var(--teal-300)',
      }}>
        <span style={{
          width: 30, height: 30, borderRadius: 'var(--r-ctrl)', background: 'var(--paper)',
          color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon name="box" size={16} />
        </span>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 11, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--t-control)', fontWeight: 800, color: 'var(--teal-800)' }}>{heading}</span>
          <Copyable value={cell.gencode} label={t('Copier le code-barres {gencode}', { gencode: cell.gencode })}>
            <span style={{ fontFamily: MONO, fontSize: 'var(--t-note)', color: 'var(--teal-800)', opacity: 0.85 }}>{cell.gencode}</span>
          </Copyable>
        </div>
        <button
          type="button" aria-label={t('Fermer la composition')} onClick={onClose}
          style={{
            width: 28, height: 28, border: 'none', borderRadius: 'var(--r-xs)', background: 'var(--paper)',
            color: 'var(--teal-800)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, padding: 0, cursor: 'pointer',
          }}
        >
          <Icon name="x" size={14} />
        </button>
      </div>

      {failure ? (
        <p role="alert" style={{ margin: 0, padding: '13px 16px', fontSize: 'var(--t-body)', color: 'var(--danger-ink)', lineHeight: 1.6 }}>
          
          {t('La composition n’a pas pu être lue : {failure}. Ce n’est pas « aucun mouvement ».', { failure })}
        </p>
      ) : !jeu ? (
        <p style={{ margin: 0, padding: '13px 16px', fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Lecture de la composition…')}</p>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) 190px' }}>
          {column(t('Entrées'), entrees, true, true)}
          {column(t('Sorties'), sorties, true, false)}
          {/* ⚠️ CENTRÉE DANS SA COLONNE, pas calée à gauche : c'est le seul chiffre que l'œil vient
              chercher, et le dessin lui donne toute la case. Aligné à gauche, il se lisait comme une
              quatrième ligne de la liste voisine. */}
          <div style={{
            padding: '13px 16px', background: 'var(--ink-50)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
          }}>
            {/* ⚠️ `letterSpacing: -0.02em` — relevé : un 30 px sans resserrement paraît plus large que
                le total teal de la grille du dessus, et rompt la parenté entre les deux. */}
            {/* ⚠️ Le stock et sa légende sont UNE phrase comptée : séparés, ils disaient « 1 pièces en stock ». */}
            <Trans k='<1>{n}</1> <2>pièce en stock</2>' vars={{ n: cell.stock }} parts={[
              (x) => <span style={{ fontSize: 'var(--t-display)', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--teal-800)', fontVariantNumeric: 'tabular-nums' }}>{x}</span>,
              (x) => <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{x}</span>,
            ]} />
            {/* ⚠️ LA SOMME DES LIGNES EST DITE, MÊME QUAND ELLE NE RETOMBE PAS. Sur les déclinaisons
                reprises, l'import a posé une écriture de bouclage : masquer l'écart ferait passer la
                composition pour une démonstration, alors que c'est un constat. */}
            {/* ⚠️ En chasse fixe et à 5 px : cette ligne est une OPÉRATION, pas une légende — le
                dessin la range avec les chiffres, pas avec le texte. */}
            {/* ⚠️ FORME RELEVÉE (`fStkSelSum`) : « 12 entrées − 3 sorties » — les COMPTES de pièces,
                pas le résultat de la soustraction. L'écart de reprise reste dit quand la somme ne
                retombe pas sur la photo (notre donnée reprise a ce cas, la maquette non). */}
            <span style={{ fontFamily: MONO, fontSize: 'var(--t-sub)', color: 'var(--ink-400)', marginTop: 5, textAlign: 'center' }}>
              {sum !== cell.stock
                ? t('{entrees} − {sorties} · écart de reprise {ecart}', { entrees: t('{n} entrée', { n: totalEntrees }), sorties: t('{n} sortie', { n: totalSorties }), ecart: cell.stock - sum })
                : t('{entrees} − {sorties}', { entrees: t('{n} entrée', { n: totalEntrees }), sorties: t('{n} sortie', { n: totalSorties }) })}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ONGLET « STOCK » — l'état de stock par déclinaison, tel que la maquette le dessine.
 *
 * ⚠️ UNE GRILLE CSS, PAS UN TABLEAU, et une LIGNE PAR COLORIS : chaque case de taille est un BOUTON
 * qui empile les trois barres. Je l'avais d'abord fait en trois rangées de tableau par coloris — le
 * rendu se ressemblait sur une taille unique et divergeait dès qu'il y en avait plusieurs.
 *
 * ⚠️ TROIS MESURES, EXACTEMENT CELLES DE LA MAQUETTE. Les données de reprise portent un quatrième
 * terme — 5 182 écritures de bouclage posées par l'import (« écart entre le grand livre du logiciel
 * précédent et son stock réel »). Elles n'ont PAS de barre : ce ne sont pas des gestes de gestion. On
 * les lit dans la composition, au clic — là où la maquette les attend.
 *
 * ⚠️ LES TAILLES SONT EN DUR : le relevé donne 15 · 14 · 13 · 12,5 · 12 · 11,5 · 11 · 10 px, qu'aucune
 * échelle du thème ne reproduit. Un jeton posé « à peu près » décale toute une colonne.
 */
export function EtatStock({ modelKey }: { modelKey: string }) {
  const [jeu, setJeu] = useState<StockStateSet | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [choisie, setChoisie] = useState<StockCell | null>(null);

  useEffect(() => {
    let vivant = true;
    setJeu(null);
    setFailure(null);
    setChoisie(null);
    api.articleStockState(modelKey)
      .then((r) => { if (vivant) setJeu(r); })
      .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [modelKey, attempt]);

  if (failure) {
    return (
      <div role="alert" style={{ padding: '18px 2px' }}>
        <p style={{ margin: '0 0 10px', color: 'var(--danger-ink)', fontSize: 'var(--t-body)', fontWeight: 600, lineHeight: 1.6 }}>
          
          {t('L’état de stock n’a pas pu être lu : {failure}', { failure })}
        </p>
        {/* ⚠️ Un tableau vide sur un échec de lecture se lirait « aucun stock » — l'inverse de la vérité. */}
        <p style={{ margin: '0 0 12px', color: 'var(--ink-500)', fontSize: 'var(--t-note)', lineHeight: 1.6 }}>
          
          {t('Rien n’est affiché ci-dessous : ces quantités viendraient de la base de ce magasin, et elle n’a pas répondu. Ce n’est pas « aucun stock ».')}
        </p>
        <button type="button" className="btn-ghost" onClick={() => setAttempt((n) => n + 1)}>{t('Réessayer')}</button>
      </div>
    );
  }
  if (!jeu) {
    return <p style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Lecture de l’état de stock…')}</p>;
  }
  if (jeu.cells.length === 0) {
    return <p style={{ margin: 0, padding: '18px 2px', color: 'var(--ink-500)', fontSize: 'var(--t-body)' }}>{t('Aucune déclinaison pour ce modèle.')}</p>;
  }

  const par = new Map(jeu.cells.map((c) => [`${c.color ?? ''}|${c.size ?? ''}`, c]));
  const valeur = (c: StockCell | undefined, m: string): number =>
    !c ? 0 : m === 'E' ? c.entries : m === 'V' ? c.sales : c.stock;
  /** La grille du dessin : coloris souple · colonne des lettres · une colonne par taille · total. */
  // ⚠️ GABARIT RELEVÉ, PAS ESTIMÉ (maquette l.16665) : `minmax(96px,1.2fr) 14px … minmax(54px,1fr) 62px`.
  // J’avais élargi la colonne de couleur (150) et le total (74), en resserrant les cases de taille à 52 —
  // or ce sont ELLES qui portent trois nombres empilés, et il y en a jusqu’à douze par rangée. Le
  // surplus pris sur la couleur se payait donc là où l’affichage est déjà le plus dense.
  const gabarit = `minmax(96px,1.2fr) 14px ${jeu.sizes.map(() => 'minmax(54px,1fr)').join(' ')} 62px`;

  return (
    <div>
      {/* ⚠️ LA GRILLE AVOUE SON AMPUTATION (correctif d'audit 2026-08-25) — même bandeau que le
          dépliage des déclinaisons, qui l'avait déjà. Sans lui, des coloris entiers disparaissaient
          sans un mot, et le pied « Stock total » se lisait comme une rupture. Le total, LUI, reste
          celui du modèle entier : il est calculé en base avant la troncature. */}
      {jeu.truncated && (
        <p role="status" style={{ margin: '0 0 8px', padding: '6px 10px', borderRadius: 6, background: 'var(--amber-bg)', color: 'var(--ink-800)', fontSize: 'var(--t-note)', lineHeight: 1.5 }}>
          <Trans k='Ce modèle porte <1>{declinaisons}</1> : seules les {n} premières sont affichées — des coloris entiers peuvent manquer de la grille, et les totaux par taille du pied ne comptent qu’elles. Seul le <2>stock total</2>, tout à droite du pied, porte sur le modèle entier : la rangée ne s’additionne donc pas.' vars={{ declinaisons: t('{n} déclinaison', { n: jeu.totalCells }), n: jeu.limit }} parts={[(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>]} /></p>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('État de stock par déclinaison')}</div>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 3 }}>
            
            {t('Un clic sur une taille ouvre la composition du stock de la déclinaison.')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexShrink: 0, paddingBottom: 2 }}>
          {MESURES.map((m) => (
            <span key={m.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-note)', color: 'var(--ink-600)' }}>
              {/* ⚠️ Un rectangle arrondi de 14 px, pas un cercle : le dessin l'aligne sur la forme des
                  barres qu'il légende — bordure 1 px au TON MOYEN (relevé), pas au ton d'encre. */}
              <span style={{ width: 14, height: 14, borderRadius: 'var(--r-xs)', background: m.fond, border: `1px solid ${m.legende}`, flexShrink: 0 }} />
              {m.key} — {t(m.label)}
            </span>
          ))}
        </div>
      </div>

      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          {/* ⚠️ `min-content` (relevé) et non une largeur fixe : la grille prend sa largeur NATURELLE
              (jusqu'à douze tailles) et c'est le conteneur qui défile. */}
          <div style={{ minWidth: 'min-content' }}>
            {/* En-tête */}
            <div style={{ display: 'grid', gridTemplateColumns: gabarit, gap: 8, padding: '10px 16px', background: 'var(--ink-50)', alignItems: 'center' }}>
              <div style={{ fontFamily: MONO, fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{fieldLabel('color')}</div>
              <div />
              {jeu.sizes.map((size) => (
                <div key={size} style={{ fontFamily: MONO, fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--ink-700)', textAlign: 'center' }}>{size || '—'}</div>
              ))}
              <div style={{ fontFamily: MONO, fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)', textAlign: 'right' }}>{t('Total')}</div>
            </div>

            {/* Une ligne par coloris */}
            {jeu.colors.map((co) => {
              const stockCouleur = jeu.sizes.reduce((n, size) => n + (par.get(`${co}|${size}`)?.stock ?? 0), 0);
              return (
                <div key={co} style={{ display: 'grid', gridTemplateColumns: gabarit, gap: 8, padding: '9px 16px', borderBottom: '1px solid var(--ink-100)', alignItems: 'center' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                      {/* ⚠️ 8 px et un rayon de 3 px — un carré adouci, pas un rond (relevé du dessin). */}
                      <span style={{ width: 8, height: 8, borderRadius: 'var(--r-r3)', background: 'var(--ink-300)', flexShrink: 0 }} />
                      <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{co || '—'}</span>
                    </div>
                    {/* ⚠️ « N en stock » vient de la PHOTO, pas d'une soustraction. */}
                    <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)', marginTop: 2, paddingLeft: 15 }}>
                      <Trans k='<1>{stockCouleur}</1> en stock' vars={{ stockCouleur }} parts={[(x) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{x}</span>]} /></div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {MESURES.map((m) => (
                      <span key={m.key} style={{ height: 22, display: 'flex', alignItems: 'center', fontFamily: MONO, fontSize: 'var(--t-micro)', fontWeight: 700, color: m.trait }}>{m.key}</span>
                    ))}
                  </div>

                  {jeu.sizes.map((size) => {
                    const c = par.get(`${co}|${size}`);
                    if (!c) {
                      // Déclinaison NON CRÉÉE — la case fantôme du dessin (tirets, « — ») : une case
                      // vide se lirait « zéro partout », l'absence doit se distinguer du néant.
                      return (
                        <div key={size} title={t('Déclinaison non créée')} style={{
                          border: '1.5px dashed var(--ink-200)', borderRadius: 'var(--r-ctrl)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          minWidth: 52, minHeight: 78, color: 'var(--ink-300)', fontSize: 'var(--t-meta)',
                        }}>—</div>
                      );
                    }
                    const ouverte = choisie?.gencode === c.gencode;
                    return (
                      // ⚠️ REPOS SANS CADRE (relevé maquette) : bordure transparente, le cadre teal-400
                      // n'apparaît qu'au survol (`.oz-stk-cell:hover`, en feuille — un style en ligne
                      // primerait sur le hover) ; seule la cellule SÉLECTIONNÉE porte le cadre teal-500
                      // et le fond teal-100 (`--on`), survol compris.
                      <button
                        key={size}
                        type="button"
                        title={t('Voir la composition du stock')}
                        onClick={() => setChoisie(ouverte ? null : c)}
                        className={`oz-stk-cell${ouverte ? ' oz-stk-cell--on' : ''}`}
                        style={{
                          display: 'flex', flexDirection: 'column', gap: 3, padding: 3,
                          borderRadius: 'var(--r-ctrl)', minWidth: 52, cursor: 'pointer',
                        }}
                      >
                        {MESURES.map((m) => {
                          const v = valeur(c, m.key);
                          // ⚠️ UNE BARRE À ZÉRO S'ASSOURDIT (relevé : `> 0` fait la couleur) : fond
                          // ink-50, chiffre gris — la couleur pleine est réservée aux cases où il
                          // s'est passé quelque chose. Un stock NÉGATIF reste assourdi (règle du
                          // dessin) : c'est le centre d'écarts qui le crie, pas cette grille.
                          const muette = v <= 0;
                          return (
                            <span key={m.key} style={{
                              height: 22, width: '100%', borderRadius: 'var(--r-xs)',
                              background: muette ? BARRE_MUETTE_BG : m.fond,
                              color: muette ? m.muetFg : m.trait,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontFamily: MONO,
                              fontSize: m.key === 'S' ? 12 : 11.5,
                              fontWeight: m.key === 'S' ? 800 : 400,
                              fontVariantNumeric: 'tabular-nums',
                            }}>{v}</span>
                          );
                        })}
                      </button>
                    );
                  })}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3, textAlign: 'right' }}>
                    {MESURES.map((m) => {
                      const sum = jeu.sizes.reduce((n, size) => n + valeur(par.get(`${co}|${size}`), m.key), 0);
                      return (
                        <span key={m.key} style={{
                          height: 22, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                          fontFamily: MONO, fontVariantNumeric: 'tabular-nums',
                          fontSize: m.key === 'S' ? 12 : 11,
                          // ⚠️ Graisse RELEVÉE : les totaux E/V sont en graisse normale, seul S est gras.
                          fontWeight: m.key === 'S' ? 800 : 400,
                          color: m.key === 'S' ? 'var(--ink-900)' : m.trait,
                        }}>
                          {/* ⚠️ ZÉRO SANS SIGNE : « −0 » n'a aucun sens, zéro n'est ni négatif ni positif. */}
                          {sum === 0 ? '0' : m.key === 'E' ? `+${sum}` : m.key === 'V' ? `-${sum}` : sum}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* Pied */}
            <div style={{ display: 'grid', gridTemplateColumns: gabarit, gap: 8, padding: '10px 16px', background: 'var(--teal-100)', alignItems: 'center' }}>
              {/* ⚠️ LA MENTION SUIT LE CHIFFRE (revue 2026-08-25). Rendre le total général exact tout en
                  laissant les cellules par taille sur les seules cases affichées a créé une rangée qui ne
                  s'additionne plus : un lecteur qui vérifie l'addition conclut à un bug du total, c'est-à-dire
                  l'inverse de la vérité. Un bandeau en haut de page ne suffit pas — on additionne ICI. */}
              <div style={{ fontSize: 'var(--t-body)', fontWeight: 800, color: 'var(--teal-800)' }}>
                
                {t('Stock total')}
                {jeu.truncated && (
                  <span style={{ display: 'block', fontWeight: 600, fontSize: 'var(--t-sub)', letterSpacing: '.02em' }}>
                    
                    {t('par taille : cases affichées · à droite : modèle entier')}
                  </span>
                )}
              </div>
              <div />
              {jeu.sizes.map((size) => (
                <div key={size} style={{ fontFamily: MONO, fontSize: 'var(--t-body)', fontWeight: 800, color: 'var(--teal-800)', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                  {jeu.colors.reduce((n, co) => n + (par.get(`${co}|${size}`)?.stock ?? 0), 0)}
                </div>
              ))}
              {/* ⚠️ 14 px ici, contre 12,5 pour les totaux par taille : le dessin donne plus de poids au
                  total général. Les aligner effacerait cette hiérarchie. Chiffres en chasse fixe (relevé). */}
              <div style={{ fontFamily: MONO, fontSize: 'var(--t-control)', fontWeight: 800, color: 'var(--teal-800)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {jeu.totalStock}
              </div>
            </div>
          </div>
        </div>
      </div>

      {choisie && <CompositionStock modelKey={modelKey} cell={choisie} onClose={() => setChoisie(null)} />}
    </div>
  );
}
